// state to React bindings
import React from "react";
import { useStore } from "zustand";
import {
    QueryClientProvider,
    useMutationState,
    useQuery,
} from "@tanstack/react-query";
import { Userpilot } from "userpilot";
import { ThemeProvider as ThemeProviderSC } from "styled-components";
import { STYLED } from "@proda-ai/ui-kit";

// external dependencies
import contentDisposition from "content-disposition";
import type { NavigateOptions, To } from "react-router-dom";
// THIS IS THE ONE AND ONLY PLACE WHERE WE SHOULD REACH DIRECTLY FOR THE GENERATED HTTP CLIENT
// eslint-disable-next-line no-restricted-imports
import { client as clientHTTP } from "@/generated/services.gen";

// APIs
import type {
    TExtractionType,
    TRowType,
    TTableLocation,
    TNewCoADefinition,
    TAnnualizationType,
} from "@/apis/odin/apiODIN";
import { createApiODIN } from "@/apis/odin/apiODIN";
import { createApiAuth } from "@/apis/auth/apiAuth";
import { createApiDatadog } from "@/apis/datadog/apiDatadog";
import { createApiThirdParty } from "@/apis/apiThirdParty/apiThirdParty";
import { createApiAps } from "@/apis/proda/aps";
import { createApiUserpilot } from "@/apis/userpilot/apiUserpilot";
import { createApiZendesk } from "@/apis/zendesk/apiZendesk";

// store
import type { TStoreRoot } from "@/store/createStoreRoot";
import { createStoreRoot } from "@/store/createStoreRoot";
import { createSelectorTableMappingsTotalNetIncome } from "@/store/tableMappings/createSelectorsTableMappings";
import { createSelectedColumnInfo } from "@/store/tableMappings/createSelectedColumnInfo";
import { getSelectedDatabasesFromLoadedDatabases } from "@/store/selectors/getSelectedDatabaseFromLoadedDatabases";
import { getInterval } from "@/store/extractionAnnualized/sliceAnnualizedExtraction";

// utils
import { chunkIdArray } from "@/utils/chunk";
import { apiErrorDetail } from "@/apis/apiError";

// views
import { createViewExportModal } from "@/ui/views/ViewExportModal";
import { createViewFileUpload } from "@/ui/views/ViewFileUpload";
import { createViewFileExtractionModal } from "@/ui/views/ViewFileExtractionModal";
import { createViewToasts } from "@/ui/views/ViewToasts";
import { createViewDealList } from "@/ui/views/ViewDealList";
import { createViewDealCreation } from "@/ui/views/ViewDealCreation";
import { createViewDashboard } from "@/ui/views/ViewDashboard";
import { createViewDealOverview } from "@/ui/views/ViewDealOverview";
import { createViewDatabaseSelector } from "@/ui/views/ViewDatabaseSelector";
import { createViewTopBar } from "@/ui/views/ViewTopBar/createViewTopBar";
import { createViewSelectDatabaseModal } from "@/ui/views/ViewDealCreation/createViewSelectDatabaseModal";
import { createViewDatabaseGuard } from "@/ui/views/ViewDatabaseGuard";
import { createViewSettingsMenu } from "@/ui/views/ViewSettingsMenu";
import { createViewDealDetails } from "@/ui/views/ViewDealDetails";
import { createViewDealActivities } from "@/ui/views/ViewDealActivities";
import { createViewUserConsentModal } from "@/ui/views/ViewUserConsentModal";
import { createViewAuthCallback } from "@/ui/views/ViewAuthCallback";
import { createViewAuthInit } from "@/ui/views/ViewAuthInit";
import { createViewAuthGuard } from "@/ui/views/ViewAuthGuard";
import { createViewThirdPartyDoc } from "@/ui/views/ViewThirdPartyDoc";
import { createViewExtractionsSidebar } from "@/ui/views/ViewExtractionsSidebar";
import { createViewExtractionMappings } from "@/ui/views/ViewExtractionMappings";
import { createViewExtractionReview } from "@/ui/views/ViewExtractionReview";
import { createViewExtractionAnnualized } from "@/ui/views/ViewExtractionAnnualized";
import { createViewConcurrentUsers } from "@/ui/views/ViewConcurrentUsers";
import { createViewNavigationSidebar } from "@/ui/views/ViewNavigationSidebar";
import { createViewNewExperienceSection } from "@/ui/views/ViewNewExperienceSection";
import { createViewChartsOfAccountsDashboard } from "./views/ViewChartsOfAccountsDashboard/createViewChartsOfAccountsDashboard";
import { createViewChartsOfAccountsDetails } from "./views/ViewChartsOfAccountsDetails/createViewChartsOfAccountsDetails";
import { createViewAuthRoleGuard } from "./views/ViewAuthRoleGuard/createViewAuthRoleGuard";
import { createViewUserPolicies } from "./views/ViewUserPolicies";
import { createViewChartsOfAccountsMappings } from "./views/ViewChartsOfAccountsMappings";
import { createViewEditChartOfAccountsDetails } from "./views/ViewEditChartOfAccountsDetails";
import { createViewEditChartOfAccountsDefinition } from "./views/ViewEditChartOfAccountsDefinition";
import { createViewChartsOfAccountsAuditLog } from "./views/ViewChartsOfAccountsAuditLog/createViewChartsOfAccountsAuditLog";
import { createViewCreateChartOfAccounts } from "./views/ViewCreateChartOfAccounts/createViewCreateChartOfAccounts";
import { createViewCoAUpload } from "./views/ViewCoAUpload/createViewCoAUpload";
import { createViewBreadcrumbs } from "./views/ViewBreadcrumbs";
import { createUseReconciliationState } from "@/ui/views/ViewExtractionAnnualized/createUseReconciliationState";
import { convertAPIResponseToDTOTableExtractionAnnualized } from "@/store/extractionAnnualized/convertAPIResponseToDTOTableExtractionAnnualized";

// UI dependencies
import type {
    TDealsOverviewTab,
    TChartsOfAccountsTab,
    TDashboardTab,
} from "@/ui/router/routerPaths";
import type {
    FileExtractionInfoWithParent,
    NewProperty,
    MultipropPropertyInfo,
    ProjectInfo,
} from "@/ui/organisms/dealTypes";
import { hasCoaLockingExtractions } from "@/ui/organisms/dealTypes";
import { isOperatingStatementUpload } from "@/ui/organisms/ExtractionTypeSelect";
import { routerPaths } from "@/ui/router/routerPaths";
import { router as navigateExporterRouter } from "@/ui/router/NavigateExporter";

// E2E tests dependencies
import {
    checkIfThisIsE2ETestsEnvironment,
    setupE2ETestsRuntimeDependencies,
} from "./setupE2ETestsDependencies";

// utils
import { rentrollPageUrl } from "@/utils/rentrollPageURL";
import {
    getEnvironment,
    areThirdPartyToolsEnabled,
    isProductionEnvironment,
} from "@/utils/getEnvironment";
import { getBaseHttpUrl } from "@/utils/getBaseHttpUrl";
import { getBaseWsUrl } from "@/utils/getBaseWsUrl";
import { getProdaBaseUrl } from "@/utils/getProdaBaseUrl";
import { isNullable } from "@/utils/guards";
import { createProdaLinks } from "@/utils/createProdaLink";
import { createCoreUILinks } from "@/utils/createProdaLink";
import { createExtractionExit } from "@/utils/extractionExit";

// configuration (environment variables)
import { configurationContainer } from "@/configurationContainer";
import { projectPageUrl } from "@/utils/projectPageURL";
import { createViewProjectDocuments } from "./views/ViewProjectDocuments/createViewProjectDocuments";
import { createQueryClient } from "@/queries/queryClient";
import { createProjectQueryHooks } from "@/queries/projects/useProjectQueries";
import { createProjectMutationHooks } from "@/queries/projects/useProjectMutations";
import { createUserQueryHooks } from "@/queries/user/useUserQueries";
import { createUserMutationHooks } from "@/queries/user/useUserMutations";
import { createDatabaseMutationHooks } from "@/queries/databases/useDatabaseMutations";
import { createDatabaseQueryHooks } from "@/queries/databases/useDatabaseQueries";
import { createConfigQueryHooks } from "@/queries/config/useConfigQueries";
import { createAnalyticsQueryHooks } from "@/queries/analytics/useAnalyticsQueries";
import { createSettingsMutationHooks } from "@/queries/settings/useSettingsMutations";
import { createAdminQueryHooks } from "@/queries/admin/useAdminQueries";
import { createCoaQueryHooks } from "@/queries/coa/useCoaQueries";
import { createCoaMutationHooks } from "@/queries/coa/useCoaMutations";
import {
    buildStandardizedExtractionQueryData,
    createExtractionQueryHooks,
} from "@/queries/extractions/useExtractionQueries";
import { createExtractionMutationHooks } from "@/queries/extractions/useExtractionMutations";
import {
    projectKeys,
    adminKeys,
    coaKeys,
    databaseKeys,
} from "@/queries/queryKeys";
import { getProjectActivityList } from "@/store/deals/getProjectActivityList";
import { transformDealView } from "@/store/deals/transformDealView";
import { getAccountOptionsWithTypes } from "@/store/accounts/getAccountOptionsWithTypes";

type MutationStatus = "idle" | "pending" | "success" | "error";

type MutationStatusSnapshot = {
    status: MutationStatus;
    submittedAt: number;
};

const getLatestMutationStatus = (
    mutations: MutationStatusSnapshot[],
): MutationStatus => {
    return (
        mutations.reduce<MutationStatusSnapshot | null>(
            (latest, mutation) =>
                latest === null || mutation.submittedAt >= latest.submittedAt
                    ? mutation
                    : latest,
            null,
        )?.status ?? "idle"
    );
};

const mutationVariablesHaveCoaId = (
    variables: unknown,
    coaId: number,
): boolean =>
    typeof variables === "object" &&
    variables !== null &&
    "coaId" in variables &&
    variables.coaId === coaId;

const llmReExtractProgressQueryKey = ["llm-reextract-progress"] as const;

const isSafeReturnUrl = (url: string): boolean => {
    try {
        const target = new URL(url, window.location.origin);
        const allowedOrigins = [
            new URL(configurationContainer.PUBLIC_CORE_URL).origin,
            new URL(configurationContainer.PUBLIC_PRODA_URL).origin,
            window.location.origin,
        ];
        return allowedOrigins.includes(target.origin);
    } catch {
        return false;
    }
};

// A warning group is toggled as a unit: if any of its warnings is already in
// the (post-toggle) dismissed set the whole group is now dismissed, otherwise
// it has just been un-dismissed. The resulting `dismissed` flag is persisted
// per outlier so the change is applied immediately rather than on save.
const toDismissOutliersPayload = (
    warnings: { rowId: number; periodId: number; accountLabel: string }[],
    rowsDismissed: { rowId: number; periodId: number; accountLabel: string }[],
): {
    rowOrder: number;
    periodId: number;
    accountLabel: string;
    dismissed: boolean;
}[] => {
    const dismissed = warnings.some(warning =>
        rowsDismissed.some(
            row =>
                row.rowId === warning.rowId &&
                row.periodId === warning.periodId &&
                row.accountLabel === warning.accountLabel,
        ),
    );
    return warnings.map(warning => ({
        rowOrder: warning.rowId,
        periodId: warning.periodId,
        accountLabel: warning.accountLabel,
        dismissed,
    }));
};

export type TRouter = {
    navigate: (to: To, options?: NavigateOptions) => void;
};

export const createCompositionRootUI = ({
    authorizationHeaderInjector: externalAuthorizationHeaderInjector,
    setDatabaseIdsOnAuthenticated: databaseIdsFromURLParams,
    currencyFromURLParam,
    router = navigateExporterRouter,
}: {
    authorizationHeaderInjector?: Parameters<
        typeof clientHTTP.interceptors.request.use
    >[0];
    setDatabaseIdsOnAuthenticated?: string[];
    currencyFromURLParam?: string;
    router?: TRouter;
} = {}) => {
    // THIS IS THE ONE AND ONLY PLACE WHERE WE SHOULD REACH DIRECTLY FOR THE USER DATE FROM THE BROWSER
    // eslint-disable-next-line no-restricted-syntax
    const getCurrentDate = () => new Date();

    const downloadFile = (file: Blob | string, fileName: string) => {
        if (typeof file === "string") {
            const a = document.createElement("a");
            a.href = file;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            return;
        }

        const url = URL.createObjectURL(file);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    const extractionExit = createExtractionExit({
        getSearch: () => window.location.search,
        isSafeReturnUrl,
        redirectTo: url => {
            window.location.href = url;
        },
        toDealDocuments: ({ projectId, databaseId }) =>
            navigate.toPageDealOverviewDocuments({
                projectId: String(projectId),
                databaseId: String(databaseId),
            }),
    });

    const navigate = {
        toAppEntrypoint: () => router.navigate(routerPaths.main.default()),
        toDealCreation: (databaseId: string) =>
            router.navigate(routerPaths.projects.create({ databaseId })),
        toPageExtractionMappings: ({
            projectId,
            databaseId,
            extractionId,
        }: {
            projectId: string;
            databaseId: string;
            extractionId: string;
        }) =>
            router.navigate(
                extractionExit.withReturnUrl(
                    routerPaths.extraction.mappings({
                        projectId,
                        databaseId,
                        extractionId,
                    }),
                ),
            ),
        navigateToPageExtractions: ({
            projectId,
            databaseId,
            extractionId,
        }: {
            projectId: string;
            databaseId: string;
            extractionId: string;
        }) => {
            router.navigate(
                extractionExit.withReturnUrl(
                    routerPaths.extraction.mappings({
                        projectId,
                        databaseId,
                        extractionId,
                    }),
                ),
            );
        },
        toPageExtractionReview: ({
            projectId,
            databaseId,
            extractionId,
        }: {
            projectId: string;
            databaseId: string;
            extractionId: string;
        }) =>
            router.navigate(
                extractionExit.withReturnUrl(
                    routerPaths.extraction.review({
                        projectId,
                        databaseId,
                        extractionId,
                    }),
                ),
            ),
        toReferer: ({ url }: { url: string | null }) =>
            router.navigate(url ?? routerPaths.main.default()),
        toPageDealOverviewDocuments: ({
            databaseId,
            projectId,
        }: {
            databaseId: string;
            projectId: string;
        }) => {
            router.navigate(
                routerPaths.projects.read({
                    databaseId,
                    projectId,
                    tab: "documents",
                }),
            );
        },
        toPageDealOverviewDocumentsWithReplace: ({
            databaseId,
            projectId,
        }: {
            databaseId: string;
            projectId: string;
        }) => {
            router.navigate(
                routerPaths.projects.read({
                    databaseId,
                    projectId,
                    tab: "documents",
                }),
                {
                    replace: true,
                },
            );
        },
        toCreateExtraction: ({
            projectId,
            fileId,
            databaseId,
            activeTab,
        }: {
            projectId: string;
            fileId: string;
            databaseId: string;
            activeTab: TDealsOverviewTab;
        }) =>
            router.navigate(
                routerPaths.files.create({
                    projectId,
                    fileId,
                    databaseId,
                    tab: activeTab,
                }),
            ),
        toUserPolicies: () =>
            router.navigate(routerPaths.userPolicies.default()),
        toChartsOfAccountsList: () =>
            router.navigate(routerPaths.chartsOfAccounts.dashboard("list")),
        toChartsOfAccountsDetails: ({ coaId }: { coaId: string }) =>
            router.navigate(routerPaths.chartsOfAccounts.view(coaId)),
        toChartOfAccountsEditDefinition: ({ coaId }: { coaId: string }) =>
            router.navigate(
                routerPaths.chartsOfAccounts.editCoA(coaId, "definition"),
            ),
        toChartOfAccountsCreate: () =>
            router.navigate(routerPaths.chartsOfAccounts.create()),
        toChartOfAccountsEditDetails: ({ coaId }: { coaId: string }) =>
            router.navigate(
                routerPaths.chartsOfAccounts.editCoA(coaId, "details"),
            ),
        toDashboard: (tab: TDashboardTab) =>
            router.navigate(routerPaths.dashboard.read(tab)),
        toChartsOfAccounts: (tab: TChartsOfAccountsTab) =>
            router.navigate(routerPaths.chartsOfAccounts.dashboard(tab)),
        toChartOfAccountsUpload: ({ coaId }: { coaId: string }) =>
            router.navigate(
                routerPaths.chartsOfAccounts.upload(coaId, "details"),
            ),
        toChartOfAccountsEditMappings: ({ coaId }: { coaId: string }) =>
            router.navigate(routerPaths.chartsOfAccounts.editMappings(coaId)),
    };

    const apiAuth = createApiAuth({
        loginCallbackPath: `${window.location.origin}${routerPaths.auth.loginCallback()}`,
        logoutReturnToPath: window.location.origin,
    });
    const apiThirdPartyAuth = createApiAuth({
        loginCallbackPath: `${window.location.origin}${routerPaths.thirdPartyDoc.default()}`,
        logoutReturnToPath: `${window.location.origin}${routerPaths.thirdPartyDoc.default()}`,
    });
    const thirdPartyToolsEnabled = areThirdPartyToolsEnabled();
    const apiZendesk = createApiZendesk({
        getAuth0Token: apiAuth.getTokenSilently,
        enabled: thirdPartyToolsEnabled,
        prodaBaseUrl: getProdaBaseUrl(),
    });
    const apiAps = createApiAps();

    const authorizationHeaderInjector: Parameters<
        typeof clientHTTP.interceptors.request.use
    >[0] = async request => {
        const ENDPOINTS_EXCLUDED_FROM_AUTH = [
            "api/initial-config",
            "api/thirdparty-auth0-config",
        ];
        const url = request.url;
        const isURLExcludedFromAuth = ENDPOINTS_EXCLUDED_FROM_AUTH.find(
            endpoint => url.includes(endpoint),
        );
        if (isURLExcludedFromAuth !== undefined) return request;

        const token =
            (await storeRoot.getState().getTokenOrRedirectToLogin({
                returnTo: `${window.location.pathname}${window.location.search}`,
            })) ??
            (await storeRoot.getState().getThirdPartyTokenOrRedirectToLogin({
                returnTo: `${window.location.pathname}${window.location.search}`,
            }));

        // If token is undefined, wait for redirect to happen before failing
        if (token === undefined || token === null) {
            await new Promise(resolve => setTimeout(resolve, 1000));
            throw new Error(
                "Authentication token unavailable - redirecting to login",
            );
        }

        request.headers.set("Authorization", `Bearer ${token}`);
        return request;
    };
    const apiODIN = createApiODIN({
        getAccessToken: apiAuth.getTokenSilently,
        baseHttpUrl: getBaseHttpUrl(),
        baseWsUrl: getBaseWsUrl(),
        requestInterceptors: [
            externalAuthorizationHeaderInjector
                ? externalAuthorizationHeaderInjector
                : authorizationHeaderInjector,
        ],
    });
    const apiThirdParty = createApiThirdParty();
    const apiDatadog = createApiDatadog({
        applicationId: configurationContainer.PUBLIC_DATADOG_RUM_APPLICATION_ID,
        clientToken: configurationContainer.PUBLIC_DATADOG_RUM_CLIENT_TOKEN,
        environment: getEnvironment(),
        enabled: thirdPartyToolsEnabled,
        apiBaseUrl: getBaseHttpUrl(),
    });
    const apiUserpilot = createApiUserpilot({
        clientToken: configurationContainer.PUBLIC_USERPILOT_TOKEN,
        environment: getEnvironment(),
        appURL: getBaseHttpUrl(),
        enabled: thirdPartyToolsEnabled,
    });

    const datadogRumHaveToBeInitializedBeforeWeWillPassFetchToTheHTTPClient =
        () => {
            apiDatadog.initRum();
            clientHTTP.setConfig({
                fetch: window.fetch,
            });
        };
    datadogRumHaveToBeInitializedBeforeWeWillPassFetchToTheHTTPClient();

    const queryClient = createQueryClient();

    // Federation hosts (e.g. core-ui) mount Odin views outside Odin's
    // standalone QueryProvider; they wrap the views once with this, bound to
    // the same client and react-query instance the view hooks read from.
    // Theme only, no GlobalStyles: globals would restyle the host document.
    const OSQueryProvider = ({ children }: { children: React.ReactNode }) => (
        <ThemeProviderSC theme={STYLED.theme}>
            <QueryClientProvider client={queryClient}>
                {children}
            </QueryClientProvider>
        </ThemeProviderSC>
    );

    const userQueryHooks = createUserQueryHooks({ apiODIN });

    const storeRoot = createStoreRoot({
        apiODIN,
        apiAuth,
        apiThirdPartyAuth,
        apiThirdParty,
        apiDatadog,
        apiUserpilot,
        apiZendesk,
        apiAps,
        navigate,
        getUserDataSnapshot: () =>
            queryClient.getQueryData<
                Awaited<
                    ReturnType<
                        ReturnType<
                            typeof userQueryHooks.getUserDataQueryOptions
                        >["queryFn"]
                    >
                >
            >(userQueryHooks.getUserDataQueryOptions().queryKey) ?? null,
    });

    const isThisE2ETestsEnvironment = checkIfThisIsE2ETestsEnvironment();
    if (isThisE2ETestsEnvironment) {
        setupE2ETestsRuntimeDependencies({
            getState: storeRoot.getState,
            apiODIN,
        });
    }

    const useRootStoreSelector = <T,>(
        selector: (state: TStoreRoot) => T,
        equalityFn?: (a: T, b: T) => boolean,
    ): T => useStore(storeRoot, selector, equalityFn);

    const addNotificationWithDefaultTimeout =
        storeRoot.getState().addNotificationWithDefaultTimeout;

    const createExtractionFromExistingFile = async ({
        fileId,
        projectId,
        databaseId,
        documentType,
        useAiExtraction,
    }: {
        fileId: number;
        projectId: number;
        databaseId: number;
        documentType: "operating_statement" | "rent_roll" | "other";
        useAiExtraction?: boolean;
    }) => {
        const store = storeRoot.getState();
        if (store.extractionState.status === "pending") {
            return;
        }
        store.startExtraction();

        Userpilot.track("odin_user_adds_new_extraction");

        if (documentType === "rent_roll") {
            Userpilot.track("os_user_attempts_rr_upload");
            try {
                store.extractionProgress({
                    progress: 0,
                    progressMessage: "Processing file...",
                });
                const response =
                    await apiODIN.createExtractionFromRentRollFileByProjectId({
                        projectId,
                        databaseId,
                        fileId,
                    });
                const {
                    hasExtraction,
                    sheetId,
                    extractionId,
                    requireExtraction,
                    excelsiorFileId,
                    error,
                } = response.data;
                if (error !== undefined && error !== null) {
                    const userSelectedDatabases =
                        queryClient.getQueryData<
                            Awaited<
                                ReturnType<
                                    ReturnType<
                                        typeof userQueryHooks.getUserDataQueryOptions
                                    >["queryFn"]
                                >
                            >
                        >(userQueryHooks.getUserDataQueryOptions().queryKey)
                            ?.userSelectedDatabases ?? [];
                    // If extraction failed but file was uploaded, navigate to tablebounds for manual selection
                    if (
                        requireExtraction === true &&
                        excelsiorFileId !== undefined &&
                        excelsiorFileId !== null
                    ) {
                        window.location.href = createProdaLinks(
                            `/rr/upload/workbooks/${excelsiorFileId}/tablebounds`,
                            userSelectedDatabases,
                        );
                        return;
                    }
                    store.extractionFailure({
                        errorMessage: `Error: ${error ?? "Error uploading file to PRODA"}`,
                    });
                    Userpilot.track("os_rentroll_upload_fails");
                    return;
                }
                Userpilot.track("os_upload_rentroll");
                const refetchProjectFiles = () =>
                    queryClient.invalidateQueries({
                        queryKey: projectKeys.files(projectId, databaseId),
                        refetchType: "all",
                    });
                const fileExists = storeRoot.getState().fileExists;
                if (fileExists && requireExtraction === false) {
                    addNotificationWithDefaultTimeout({
                        kind: "toast",
                        title: "File already exists. Extraction will continue in the background.",
                        severity: "info",
                    });
                    await refetchProjectFiles();
                    navigate.toPageDealOverviewDocuments({
                        projectId: String(projectId),
                        databaseId: String(databaseId),
                    });
                    return;
                } else if (fileExists) {
                    addNotificationWithDefaultTimeout({
                        kind: "toast",
                        title: "Creating a new extraction as the file already exists.",
                        severity: "info",
                    });
                } else if (requireExtraction === false) {
                    addNotificationWithDefaultTimeout({
                        kind: "toast",
                        title: "Sent for processing",
                        message: `
                            Your file is being processed by PRODA. We'll email you as soon as it's ready for the next step.
                        `,
                        severity: "info",
                    });
                    await refetchProjectFiles();
                    navigate.toPageDealOverviewDocuments({
                        projectId: String(projectId),
                        databaseId: String(databaseId),
                    });
                    return;
                }
                store.extractionProgress({
                    progress: 100,
                    progressMessage: "File processed successfully.",
                });
                store.extractionSuccess({
                    extractionId: 0,
                    allFoundTables: [],
                });
                store.resetStateSliceAddExtraction();
                const userSelectedDatabases =
                    queryClient.getQueryData<
                        Awaited<
                            ReturnType<
                                ReturnType<
                                    typeof userQueryHooks.getUserDataQueryOptions
                                >["queryFn"]
                            >
                        >
                    >(userQueryHooks.getUserDataQueryOptions().queryKey)
                        ?.userSelectedDatabases ?? [];
                if (
                    hasExtraction &&
                    sheetId !== null &&
                    extractionId !== null &&
                    excelsiorFileId !== undefined &&
                    excelsiorFileId !== null
                ) {
                    window.location.href = createProdaLinks(
                        `/rr/upload/workbooks/${excelsiorFileId}/worksheets/${sheetId}/rentrolls/${extractionId}/edit`,
                        userSelectedDatabases,
                    );
                    return;
                }
                if (
                    requireExtraction === true &&
                    excelsiorFileId !== undefined &&
                    excelsiorFileId !== null
                ) {
                    window.location.href = createProdaLinks(
                        `/rr/upload/workbooks/${excelsiorFileId}/tablebounds`,
                        userSelectedDatabases,
                    );
                    return;
                }
                await refetchProjectFiles();
                navigate.toPageDealOverviewDocuments({
                    projectId: String(projectId),
                    databaseId: String(databaseId),
                });
                return;
            } catch (error) {
                store.extractionFailure({
                    errorMessage: (error as Error).message,
                    isExtractionNetworkError: error instanceof TypeError,
                });
                return;
            }
        }

        void apiODIN
            .createExtractionFromExistingFile({
                fileId,
                useAiExtraction,
                onExtractionSucceeded(message) {
                    const { extractionId, allFoundTables } = message;
                    storeRoot
                        .getState()
                        .extractionSuccess({ extractionId, allFoundTables });
                    Userpilot.track("odin_user_completes_extraction");
                },
                onProgress: ({ progress, message }) => {
                    storeRoot.getState().extractionProgress({
                        progress,
                        progressMessage: message,
                    });
                },
                onError({ errorMessage }) {
                    storeRoot.getState().extractionFailure({ errorMessage });
                },
                onConnectionClosed() {
                    const currentStore = storeRoot.getState();
                    const isResolved =
                        currentStore.extractionState.status === "resolved";
                    const isRejected =
                        currentStore.extractionState.status === "rejected";
                    const requestIsCompletedBeforeConnectionIsClosed =
                        isResolved || isRejected;

                    if (!requestIsCompletedBeforeConnectionIsClosed) {
                        currentStore.extractionFailure({
                            errorMessage:
                                "The connection was interrupted before the process could finish.",
                        });
                        return;
                    }
                    if (!isResolved) return;
                    const { extractionId } = currentStore.extractionState
                        .data as {
                        extractionId: number;
                        allFoundTables: unknown[];
                    };
                    navigate.navigateToPageExtractions({
                        projectId: String(projectId),
                        databaseId: String(databaseId),
                        extractionId: String(extractionId),
                    });
                },
            })
            .catch((error: unknown) => {
                // getAccessToken (and anything before the WebSocket handlers are
                // wired) can reject without firing onError/onConnectionClosed,
                // which would leave extractionState stuck on "pending". Mirror the
                // old sliceAddExtraction try/catch so the upload UI still shows
                // failure; skip if a callback already settled the state.
                const status = storeRoot.getState().extractionState.status;
                if (status === "resolved" || status === "rejected") return;
                storeRoot.getState().extractionFailure({
                    errorMessage:
                        error instanceof Error
                            ? error.message
                            : "There was an error processing the file.",
                    isExtractionNetworkError: error instanceof TypeError,
                });
            });
    };

    const projectQueryHooks = createProjectQueryHooks({ apiODIN });
    const projectMutationHooks = createProjectMutationHooks({
        apiODIN,
        queryClient,
        addNotificationWithDefaultTimeout,
    });
    const userMutationHooks = createUserMutationHooks({
        apiODIN,
        queryClient,
        addNotificationWithDefaultTimeout,
    });
    const databaseMutationHooks = createDatabaseMutationHooks({
        apiODIN,
        queryClient,
        addNotificationWithDefaultTimeout,
    });
    const databaseQueryHooks = createDatabaseQueryHooks({ apiODIN });
    const configQueryHooks = createConfigQueryHooks({ apiODIN });
    const analyticsQueryHooks = createAnalyticsQueryHooks({ apiODIN });
    const settingsMutationHooks = createSettingsMutationHooks({
        apiODIN,
        queryClient,
        addNotificationWithDefaultTimeout,
        addNotification: storeRoot.getState().addNotification,
        removeNotification: storeRoot.getState().removeNotification,
    });
    const adminQueryHooks = createAdminQueryHooks({ apiODIN });
    const coaQueryHooks = createCoaQueryHooks({ apiODIN });
    const coaMutationHooks = createCoaMutationHooks({
        apiODIN,
        queryClient,
        addNotificationWithDefaultTimeout,
    });
    const extractionQueryHooks = createExtractionQueryHooks({
        apiODIN,
    });
    const extractionMutationHooks = createExtractionMutationHooks({
        apiODIN,
        queryClient,
        addNotificationWithDefaultTimeout,
    });

    const useAuthState = () => {
        const authUser = useRootStoreSelector(state => state.authUser);
        const authConfigState = useRootStoreSelector(
            state => state.initialConfigState,
        );
        const authInitState = useRootStoreSelector(
            state => state.authInitState,
        );
        const authSessionState = useRootStoreSelector(
            state => state.authSessionState,
        );
        const authTokenState = useRootStoreSelector(
            state => state.authTokenState,
        );
        const authRedirectState = useRootStoreSelector(
            state => state.authRedirectState,
        );
        const authLoginState = useRootStoreSelector(
            state => state.authLoginState,
        );
        const authLogoutState = useRootStoreSelector(
            state => state.authLogoutState,
        );

        const authState = {
            authUser,
            authConfigState,
            authInitState,
            authSessionState,
            authTokenState,
            authRedirectState,
            authLoginState,
            authLogoutState,
        };

        const isAuthLoading = useRootStoreSelector(
            state => state.isAuthLoading,
        )(authState);
        const isAuthenticated = useRootStoreSelector(
            state => state.isAuthenticated,
        )(authState);
        const authError = useRootStoreSelector(
            state => state.initialConfigState.error,
        );
        const isInitializingAuth = useRootStoreSelector(
            state => state.isInitializingAuth,
        )(authState);

        return {
            user: authUser,
            isAuthenticated,
            isLoading: isAuthLoading,
            isInitializingAuth,
            error: authError,
        };
    };

    const ViewAuthInit = createViewAuthInit({
        useState: useAuthState,
        useCases: () => {
            const initializeInitialConfig = useRootStoreSelector(
                state => state.initializeInitialConfig,
            );
            const setDatadogUser = useRootStoreSelector(
                state => state.setDatadogUser,
            );
            const initializeUserpilot = useRootStoreSelector(
                state => state.initializeUserpilot,
            );

            const initializeZendesk = useRootStoreSelector(
                state => state.initializeZendesk,
            );
            const setSelectedDatabasesMutation =
                databaseMutationHooks.useSetSelectedDatabasesMutation();

            const onMount = React.useCallback(() => {
                void initializeInitialConfig();
            }, [initializeInitialConfig]);

            const onAuthenticated = React.useCallback(
                async ({ email }: { email?: string }) => {
                    console.debug(`Logged in as ${email}!`);
                    if (
                        databaseIdsFromURLParams &&
                        databaseIdsFromURLParams.length > 0
                    ) {
                        const databaseIds =
                            databaseIdsFromURLParams.map(Number);
                        await setSelectedDatabasesMutation.mutateAsync({
                            selectedDatabaseIds: databaseIds,
                            notifyUser: true,
                        });
                    }
                    const userData = await queryClient.ensureQueryData(
                        userQueryHooks.getUserDataQueryOptions(),
                    );
                    if (userData?.userExportConfig) {
                        storeRoot.getState().loadExportConfig({
                            exportConfig: userData.userExportConfig,
                        });
                    }
                    void initializeZendesk();
                    initializeUserpilot();
                    setDatadogUser();
                    Userpilot.track("odin_user_logged_in");
                    Userpilot.track("userpilot_initialised");
                },
                [
                    setDatadogUser,
                    initializeZendesk,
                    initializeUserpilot,
                    setSelectedDatabasesMutation,
                ],
            );
            return {
                onMount,
                onAuthenticated,
            };
        },
    });

    // Shared CoA-prompt wiring for the two OS-upload surfaces (fresh upload and
    // add-extraction-to-existing-file), so they cannot drift apart. Queries are
    // enabled lazily — only once the upload is known to be an Operating
    // Statement one.
    const useAssignCoa = () => {
        const { mutateAsync: updateProject } =
            projectMutationHooks.useUpdateProjectMutation();
        return React.useCallback(
            async ({
                projectId,
                databaseId,
                coaId,
                dealName,
                description,
            }: {
                projectId: number;
                databaseId: number;
                coaId: number;
                dealName: string;
                description: string | null | undefined;
            }) => {
                return await updateProject({
                    projectId,
                    databaseId,
                    coaId,
                    dealName,
                    description,
                });
            },
            [updateProject],
        );
    };
    const useCoaPromptState = ({
        projectId,
        databaseId,
        isOsUpload,
    }: {
        projectId: number;
        databaseId: number;
        isOsUpload: boolean;
    }) => {
        const {
            data: filesData,
            isPending: filesIsPending,
            isError: filesIsError,
            refetch: refetchFiles,
        } = projectQueryHooks.useProjectFilesQuery(projectId, databaseId, {
            enabled: isOsUpload,
        });
        const {
            data: projectDetail,
            isPending: projectDetailIsPending,
            isError: projectDetailIsError,
            refetch: refetchProjectDetail,
        } = projectQueryHooks.useProjectDetailQuery(projectId, databaseId, {
            enabled: isOsUpload,
        });
        const {
            data: availableCoAsData,
            isPending: availableCoAsIsPending,
            isError: availableCoAsIsError,
            refetch: refetchAvailableCoAs,
        } = coaQueryHooks.useCoasByDatabaseQuery(databaseId, {
            enabled: isOsUpload,
        });

        return {
            isOsUpload,
            projectCoaId: projectDetail?.coaId ?? null,
            projectDealName: projectDetail?.dealName ?? null,
            projectDescription: projectDetail?.description ?? null,
            availableCoAs: availableCoAsData ?? [],
            availableCoAsStatus: availableCoAsIsPending
                ? ("pending" as const)
                : availableCoAsIsError
                  ? ("rejected" as const)
                  : ("resolved" as const),
            // A failed files/detail fetch must surface as a recoverable error
            // (never a stuck spinner), so error wins over pending.
            baselineStatus:
                filesIsError || projectDetailIsError
                    ? ("rejected" as const)
                    : filesIsPending || projectDetailIsPending
                      ? ("pending" as const)
                      : ("resolved" as const),
            coaLocked: hasCoaLockingExtractions(filesData?.filteredFiles ?? []),
            retryCoaData: () => {
                void refetchFiles();
                void refetchProjectDetail();
                void refetchAvailableCoAs();
            },
        };
    };

    const ViewFileUpload = createViewFileUpload({
        useCases: () => {
            const uploadFile = useRootStoreSelector(state => state.uploadFile);
            const { mutateAsync: addDealFileMutateAsync } =
                projectMutationHooks.useAddProjectFileMutation();
            const setDocumentType = useRootStoreSelector(
                state => state.setDocumentType,
            );
            const setFileName = useRootStoreSelector(
                state => state.setFileName,
            );
            const setStep = useRootStoreSelector(state => state.setStep);

            const startExtractionFromExistingFile = async ({
                fileId,
                projectId,
                fileName,
                documentType,
                databaseId,
                useAiExtraction,
            }: {
                fileId: number;
                projectId: number;
                fileName: string;
                documentType: "operating_statement" | "rent_roll";
                databaseId: number;
                useAiExtraction?: boolean;
            }) => {
                setFileName(fileName);
                setStep("extract");
                await createExtractionFromExistingFile({
                    fileId,
                    projectId,
                    databaseId,
                    documentType,
                    useAiExtraction,
                });
                // Refresh only this project's file list (the new extraction
                // lives there). A CoA assign, when one happened, already
                // invalidated the full projects prefix via its mutation.
                void queryClient.invalidateQueries({
                    queryKey: projectKeys.files(projectId, databaseId),
                });
            };

            const onUploadFilesTriggered = async ({
                files,
                projectId,
                databaseId,
            }: {
                files: FileList;
                projectId: number;
                databaseId: number;
            }) => {
                Userpilot.track("odin_user_completes_document_upload");
                await uploadFile({
                    files,
                    projectId,
                    databaseId,
                    addDealFile: addDealFileMutateAsync,
                });
                // Note: when Rent Roll upload is disabled the store lands on
                // step "extract" here, but the extraction is NOT fired from
                // this handler — the view's CoA gate (OS-3938/OS-3983,
                // useCoaAutoFire) starts it once the CoA checks allow.
            };
            const resetStateSliceAddExtraction = useRootStoreSelector(
                state => state.resetStateSliceAddExtraction,
            );
            const resetStateUploadFile = useRootStoreSelector(
                state => state.resetStateUploadFile,
            );
            const resetFileUploadState = React.useCallback(() => {
                resetStateSliceAddExtraction();
                resetStateUploadFile();
            }, [resetStateSliceAddExtraction, resetStateUploadFile]);

            const onUnmount = React.useCallback(() => {
                resetStateSliceAddExtraction();
            }, [resetStateSliceAddExtraction]);

            const assignCoa = useAssignCoa();

            return {
                onUploadFilesTriggered,
                resetFileUploadState,
                onUnmount,
                createExtractionFromExistingFile:
                    startExtractionFromExistingFile,
                setDocumentType,
                assignCoa,
            };
        },
        useState: ({ projectId, databaseId }) => {
            const step = useRootStoreSelector(state => state.step);
            const fileUploadRequestStatus = useRootStoreSelector(
                state => state.uploadState.status,
            );
            const extractionRequestStatus = useRootStoreSelector(
                state => state.extractionState.status,
            );
            const status =
                step === "upload"
                    ? fileUploadRequestStatus
                    : extractionRequestStatus;

            const progressMessage = useRootStoreSelector(
                state => state.extractionState.progressMessage,
            );
            const selectedFileName = useRootStoreSelector(
                state => state.fileName,
            );

            const progressPercentage = useRootStoreSelector(
                state => state.extractionState.progress ?? 0,
            );
            const uploadError = useRootStoreSelector(
                state => state.uploadState.error,
            );
            const extractionError = useRootStoreSelector(
                state => state.extractionState.error,
            );
            const error = step === "upload" ? uploadError : extractionError;
            const prodaRentrollURL = rentrollPageUrl();
            const fileId = useRootStoreSelector(state => state.fileId);
            const documentType = useRootStoreSelector(
                state => state.documentType,
            );
            const { data: userData } = userQueryHooks.useUserDataQuery();
            const isRentRollUploadEnabled =
                userData?.isRentRollUploadEnabled ?? false;

            const coaPromptState = useCoaPromptState({
                projectId,
                databaseId,
                isOsUpload: isOperatingStatementUpload(
                    isRentRollUploadEnabled,
                    documentType,
                ),
            });

            return {
                status,
                selectedFileName,
                progressPercentage,
                progressMessage,
                error,
                prodaRentrollURL,
                step,
                fileId,
                documentType,
                isAiExtractionEnabled: userData?.aiFeaturesEnabled ?? false,
                isRentRollUploadEnabled,
                ...coaPromptState,
            };
        },
    });

    const ViewCoAUpload = createViewCoAUpload({
        useState: () => {
            const uploadCoAStatus = useRootStoreSelector(
                state => state.uploadCoAState.status,
            );
            const progressMessage = useRootStoreSelector(
                state => state.uploadCoAState.progressMessage,
            );
            const selectedFileName = useRootStoreSelector(
                state => state.coaFileName,
            );
            const progressPercentage = useRootStoreSelector(
                state => state.uploadCoAState.progress ?? 0,
            );
            const error = useRootStoreSelector(
                state => state.uploadCoAState.error,
            );
            const prodaRentrollURL = rentrollPageUrl();

            return {
                uploadCoAStatus,
                selectedFileName,
                progressPercentage,
                progressMessage,
                error,
                prodaRentrollURL,
            };
        },
        useCases: () => {
            const resetStateUploadCoA = useRootStoreSelector(
                state => state.resetStateUploadCoA,
            );
            const setCoAFileName = useRootStoreSelector(
                state => state.setCoAFileName,
            );
            const setCoAFileId = useRootStoreSelector(
                state => state.setCoAFileId,
            );
            const startUploading = useRootStoreSelector(
                state => state.startUploading,
            );
            const setUploadProgress = useRootStoreSelector(
                state => state.setUploadProgress,
            );
            const setUploadSuccess = useRootStoreSelector(
                state => state.setUploadSuccess,
            );
            const setUploadFailure = useRootStoreSelector(
                state => state.setUploadFailure,
            );
            const checkFileValidity = useRootStoreSelector(
                state => state.checkFileValidity,
            );
            const { mutateAsync: addCoAFileMutateAsync } =
                coaMutationHooks.useAddCoAFileMutation();
            const onUploadCoATriggered = async ({
                files,
                coaId,
            }: {
                files: FileList;
                coaId: number | null;
            }) => {
                let file: File;
                try {
                    file = checkFileValidity({ files });
                } catch (error) {
                    setUploadFailure((error as Error).message);
                    return;
                }

                let response: { fileId: number | null; existing: boolean };
                try {
                    response = await addCoAFileMutateAsync({ coaId, file });
                } catch (error) {
                    setUploadFailure((error as Error).message);
                    return;
                }

                if (response.existing) {
                    addNotificationWithDefaultTimeout({
                        kind: "toast",
                        title: "Creating a new extraction as the file already exists.",
                        severity: "info",
                    });
                }

                setCoAFileId(response.fileId);
                setCoAFileName(file.name);

                const fileId = response.fileId;
                if (fileId === null) {
                    setUploadFailure("Error: The file could not be uploaded.");
                    return;
                }

                startUploading();

                await apiODIN.extractAccounts({
                    coaId,
                    fileId,
                    onExtractionSucceeded(message) {
                        const { coaId, allFoundTables } = message;
                        setUploadSuccess({ coaId, allFoundTables });
                    },
                    onProgress: ({ progress, message }) => {
                        setUploadProgress({
                            progress,
                            progressMessage: message,
                        });
                    },
                    onError({ errorMessage }) {
                        setUploadFailure(errorMessage);
                    },
                    onConnectionClosed() {
                        const isResolved =
                            storeRoot.getState().uploadCoAState.status ===
                            "resolved";
                        const isRejected =
                            storeRoot.getState().uploadCoAState.status ===
                            "rejected";
                        const requestIsCompletedBeforeConnectionIsClosed =
                            isResolved || isRejected;

                        if (!requestIsCompletedBeforeConnectionIsClosed) {
                            setUploadFailure(
                                "The connection was interrupted before the process could finish.",
                            );
                            return;
                        }

                        if (!isResolved) return;
                        const { coaId } = storeRoot.getState().uploadCoAState
                            .data as {
                            coaId: number;
                            allFoundTables: unknown[];
                        };
                        navigate.toChartOfAccountsEditDetails({
                            coaId: String(coaId),
                        });
                    },
                });

                if (coaId !== null) {
                    void queryClient.invalidateQueries({
                        queryKey: coaKeys.all,
                    });
                }
            };
            return {
                onUploadCoATriggered,
                onMount: resetStateUploadCoA,
            };
        },
    });

    const ViewFileExtractionModal = createViewFileExtractionModal({
        useCases: () => {
            const resetStateSliceAddExtraction = useRootStoreSelector(
                state => state.resetStateSliceAddExtraction,
            );
            const resetStateUploadFile = useRootStoreSelector(
                state => state.resetStateUploadFile,
            );
            const onClose = ({
                projectId,
                databaseId,
            }: {
                projectId: number;
                databaseId: number;
            }) => {
                navigate.toPageDealOverviewDocumentsWithReplace({
                    projectId: projectId.toString(),
                    databaseId: databaseId.toString(),
                });
            };
            const setDocumentType = useRootStoreSelector(
                state => state.setDocumentType,
            );

            // Mount and unmount both clear the shared upload/extraction slices:
            // mount, so state left behind by another surface (e.g. a stale
            // documentType from ViewFileUpload) can't leak into this flow.
            const resetUploadSlices = React.useCallback(() => {
                resetStateSliceAddExtraction();
                resetStateUploadFile();
            }, [resetStateSliceAddExtraction, resetStateUploadFile]);

            const assignCoa = useAssignCoa();

            return {
                onMount: resetUploadSlices,
                onUnmount: resetUploadSlices,
                onClose,
                setDocumentType,
                createExtractionFromExistingFile,
                assignCoa,
            };
        },
        useState: ({ projectId, databaseId }) => {
            const fileExtractionRequestStatus = useRootStoreSelector(
                state => state.extractionState.status,
            );

            const { data: filesData } = projectQueryHooks.useProjectFilesQuery(
                projectId,
                databaseId,
            );

            const progressMessage = useRootStoreSelector(
                state => state.extractionState.progressMessage,
            );

            const progressPercentage = useRootStoreSelector(
                state => state.extractionState.progress ?? 0,
            );
            const error = useRootStoreSelector(
                state => state.extractionState.error,
            );
            const prodaRentrollURL = rentrollPageUrl();
            const documentType = useRootStoreSelector(
                state => state.documentType,
            );
            const { data: userData } = userQueryHooks.useUserDataQuery();
            const isRentRollUploadEnabled =
                userData?.isRentRollUploadEnabled ?? false;

            const coaPromptState = useCoaPromptState({
                projectId,
                databaseId,
                isOsUpload: isOperatingStatementUpload(
                    isRentRollUploadEnabled,
                    documentType,
                ),
            });

            return {
                fileExtractionRequestStatus,
                documentType,
                progressPercentage,
                progressMessage,
                error,
                prodaRentrollURL,
                isRentRollUploadEnabled,
                fileExtractionInfo: filesData?.transformedFileView ?? [],
                isAiExtractionEnabled: userData?.aiFeaturesEnabled ?? false,
                ...coaPromptState,
            };
        },
    });

    const ViewBreadcrumbs = createViewBreadcrumbs({
        useState: () => {
            const { data: userData } = userQueryHooks.useUserDataQuery();
            const userSelectedDatabases = React.useMemo(
                () => userData?.userSelectedDatabases ?? [],
                [userData?.userSelectedDatabases],
            );
            const availableDatabases = React.useMemo(
                () => userData?.availableDatabases ?? [],
                [userData?.availableDatabases],
            );
            const selectedDatabases = React.useMemo(
                () =>
                    availableDatabases.filter(database =>
                        userSelectedDatabases.includes(database.databaseId),
                    ),
                [availableDatabases, userSelectedDatabases],
            );
            const { data: allProjects } = projectQueryHooks.useProjectsQuery(
                userSelectedDatabases,
            );
            return {
                selectedDatabases,
                allProjects: allProjects ?? null,
            };
        },
    });

    const ViewDatabaseSelector = createViewDatabaseSelector({
        useState: () => {
            const {
                data: userData,
                isPending,
                isError,
                isSuccess: initialUserDataResolved,
            } = userQueryHooks.useUserDataQuery();
            const userSelectedDatabases = userData?.userSelectedDatabases ?? [];
            const availableDatabases = userData?.availableDatabases ?? [];
            const selectedDatabaseIdFromLoadedDatabases =
                getSelectedDatabasesFromLoadedDatabases({
                    userSelectedDatabases,
                    availableDatabases,
                });
            const selectedDatabaseIds = React.useMemo(
                () =>
                    selectedDatabaseIdFromLoadedDatabases.map(
                        database => database.databaseId,
                    ),
                [selectedDatabaseIdFromLoadedDatabases],
            );

            return {
                databasesLoadStatus: isPending
                    ? "pending"
                    : isError
                      ? "rejected"
                      : initialUserDataResolved
                        ? "resolved"
                        : "idle",
                userDatabases: availableDatabases,
                selectedDatabaseIds,
                initialUserDataResolved,
            };
        },
        useCases: () => {
            const setSelectedDatabasesMutation =
                databaseMutationHooks.useSetSelectedDatabasesMutation();

            const onSelectedDatabases = React.useCallback(
                async (selectedDatabaseIds: number[]) => {
                    await setSelectedDatabasesMutation.mutateAsync({
                        selectedDatabaseIds,
                    });
                    navigate.toAppEntrypoint();
                },
                [setSelectedDatabasesMutation],
            );

            return {
                onSelectedDatabases,
            };
        },
    });

    const ViewDealList = createViewDealList({
        useState: () => {
            const { data: userData } = userQueryHooks.useUserDataQuery();
            const userRegion = userData?.userRegion ?? null;
            const userSelectedDatabases = React.useMemo(
                () => userData?.userSelectedDatabases ?? [],
                [userData?.userSelectedDatabases],
            );
            const availableDatabases = React.useMemo(
                () => userData?.availableDatabases ?? [],
                [userData?.availableDatabases],
            );
            const userType = userData?.userInfo?.userType;
            const showDatabaseSelectionModal = useRootStoreSelector(
                state => state.showDatabaseSelectionModal,
            );
            const selectedDatabaseIdFromLoadedDatabases =
                getSelectedDatabasesFromLoadedDatabases({
                    userSelectedDatabases,
                    availableDatabases,
                });
            const {
                data: projects,
                isPending: projectsIsPending,
                isError: projectsIsError,
            } = projectQueryHooks.useProjectsQuery(userSelectedDatabases);
            const dealSearchPhrase = useRootStoreSelector(
                state => state.dealSearchPhrase,
            );
            const dealExtractionInfo = React.useMemo(
                () =>
                    transformDealView({
                        deals: projects ?? [],
                        userDatabases: availableDatabases,
                        searchPhrase: dealSearchPhrase,
                    }),
                [projects, availableDatabases, dealSearchPhrase],
            );
            const savedGridState = useRootStoreSelector(
                state => state.savedGridState,
            );
            const saveGridStatus = useRootStoreSelector(
                state => state.saveGridStatus,
            );

            return {
                dealExtractionInfo: dealExtractionInfo,
                selectedDatabases:
                    selectedDatabaseIdFromLoadedDatabases ?? null,
                showDatabaseSelectionModal,
                savedGridState: savedGridState ?? undefined,
                projectsIsPending,
                projectsIsError,
                saveGridStatus,
                dealSearchPhrase,
                userRegion,
                userType,
            };
        },
        useCases: () => {
            const deleteProjectMutation =
                projectMutationHooks.useDeleteProjectMutation();
            const deleteExtractionMutation =
                projectMutationHooks.useDeleteExtractionsMutation();
            const showExportModal = useRootStoreSelector(
                state => state.showExportModal,
            );
            const exportExtraction = ({
                extractions,
                projectId,
                databaseId,
            }: {
                extractions: {
                    extractionId: number;
                    extractionType: TExtractionType;
                }[];
                projectId: number;
                databaseId: number;
            }) => {
                showExportModal({
                    extractions,
                    projectId,
                    databaseId,
                });
            };
            const setShowDatabaseSelectionModal = useRootStoreSelector(
                state => state.setShowDatabaseSelectionModal,
            );
            const setDealCreationDatabaseId = useRootStoreSelector(
                state => state.setDealCreationDatabaseId,
            );
            const resetViewButtonClicked = useRootStoreSelector(
                state => state.resetViewButtonClicked,
            );
            const saveGridState = useRootStoreSelector(
                state => state.saveGridState,
            );
            const setDealSearchPhrase = useRootStoreSelector(
                state => state.setDealSearchPhrase,
            );
            const initialiseSavedGridView = useRootStoreSelector(
                state => state.initialiseSavedGridView,
            );
            React.useEffect(() => {
                initialiseSavedGridView();
            }, [initialiseSavedGridView]);

            const addNotificationWithDefaultTimeout = useRootStoreSelector(
                state => state.addNotificationWithDefaultTimeout,
            );
            const resolveRentRollLocation = async (params: {
                projectId: number;
                databaseId: number;
                extractionId: number;
            }) => {
                try {
                    const { data } = await apiODIN.getRentRollLocation(params);
                    return data;
                } catch (error) {
                    addNotificationWithDefaultTimeout({
                        title: "Couldn't open the rent roll",
                        kind: "toast",
                        severity: "danger",
                    });
                    console.error(error);
                    return null;
                }
            };

            return {
                deleteProjectMutation,
                deleteExtractionMutation,
                exportExtraction,
                setShowDatabaseSelectionModal,
                setDealCreationDatabaseId,
                resetViewButtonClicked,
                saveGridState,
                setDealSearchPhrase,
                resolveRentRollLocation,
            };
        },
    });

    const ViewDashboard = createViewDashboard({
        useState: () => {
            const dashboardFilter = useRootStoreSelector(
                state => state.dashboardFilter,
            );
            const { data: userData } = userQueryHooks.useUserDataQuery();
            const isProdaUser = userData?.userInfo?.isProdaUser ?? false;
            const {
                data: dashboardAnalytics,
                isPending: analyticsIsPending,
                isError: analyticsIsError,
            } = analyticsQueryHooks.useDashboardAnalyticsQuery(
                dashboardFilter,
                { enabled: isProdaUser },
            );
            const dashboardAnalyticsLoadStatus = !isProdaUser
                ? "idle"
                : analyticsIsPending
                  ? "pending"
                  : analyticsIsError
                    ? "rejected"
                    : "resolved";
            return {
                dashboardAnalyticsLoadStatus,
                dashboardAnalytics: dashboardAnalytics ?? null,
                dashboardFilter,
                isProdaUser,
            };
        },
        useCases: () => {
            const setDashboardFilter = useRootStoreSelector(
                state => state.setDashboardFilter,
            );
            const handleBackButtonClicked = navigate.toAppEntrypoint;
            return {
                setDashboardFilter,
                handleBackButtonClicked,
            };
        },
        getCurrentDate,
    });

    const ViewDealCreation = createViewDealCreation({
        useState: ({ databaseId }: { databaseId: number }) => {
            const currentDeal = useRootStoreSelector(
                state => state.dealCreation.deal,
            );
            const currentProperties = useRootStoreSelector(
                state => state.dealCreation.properties,
            );
            const editingProperty = useRootStoreSelector(
                state => state.dealCreation.editingProperty,
            );
            const currentStep = useRootStoreSelector(
                state => state.dealCreation.currentStep,
            );
            const propertyAddressCheckMutation =
                projectMutationHooks.usePropertyAddressCheckMutation();
            const propertyAddressCheck =
                propertyAddressCheckMutation.data ?? null;
            const {
                data: googleMapsConfig,
                isPending: googleMapsIsPending,
                isError: googleMapsIsError,
            } = configQueryHooks.useGoogleMapsConfigQuery();
            const configGoogleMapsState = {
                status: googleMapsIsPending
                    ? ("pending" as const)
                    : googleMapsIsError
                      ? ("rejected" as const)
                      : ("resolved" as const),
                data: googleMapsConfig ?? null,
            };
            const {
                data: availableCoAsData,
                isPending: availableCoAsIsPending,
                isError: availableCoAsIsError,
            } = coaQueryHooks.useCoasByDatabaseQuery(databaseId);
            const availableCoAs = availableCoAsData ?? null;
            const availableCoAsStatus = availableCoAsIsPending
                ? "pending"
                : availableCoAsIsError
                  ? "rejected"
                  : "resolved";
            return {
                currentDeal,
                currentProperties,
                editingProperty,
                currentStep,
                propertyAddressCheck,
                configGoogleMapsState,
                availableCoAs,
                availableCoAsStatus,
            };
        },
        useCases: () => {
            const previousStep = useRootStoreSelector(
                state => state.previousStep,
            );
            const nextStep = useRootStoreSelector(state => state.nextStep);
            const { mutateAsync: checkProjectName } =
                projectMutationHooks.useProjectNameCheckMutation();
            const setDealNameCheck = React.useCallback(
                async (databaseId: number, dealName: string) =>
                    await checkProjectName({
                        databaseId,
                        dealName,
                    }),
                [checkProjectName],
            );
            const { mutateAsync: checkPropertyAddress } =
                projectMutationHooks.usePropertyAddressCheckMutation();
            const setPropertyAddressCheck = React.useCallback(
                async (data: Parameters<typeof checkPropertyAddress>[0]) => {
                    return await checkPropertyAddress(data);
                },
                [checkPropertyAddress],
            );
            const { mutateAsync: checkPropertyName } =
                projectMutationHooks.usePropertyNameCheckMutation();
            const dealCreationProperties = useRootStoreSelector(
                state => state.dealCreation.properties,
            );
            const setPropertyNameCheck = React.useCallback(
                async (databaseId: number, propertyName: string) =>
                    await checkPropertyName({
                        databaseId,
                        propertyName,
                        otherPropertyNames: dealCreationProperties.map(
                            property => property.propertyName,
                        ),
                    }),
                [checkPropertyName, dealCreationProperties],
            );
            const onMount = React.useCallback(() => {
                // CoA data is loaded via TanStack Query hook
            }, []);

            const resetDealCreationState = useRootStoreSelector(
                state => state.resetDealCreationState,
            );
            const onUnmount = React.useCallback(() => {
                resetDealCreationState();
            }, [resetDealCreationState]);

            const setDeal = useRootStoreSelector(
                state => state.dealCreationSetDeal,
            );
            const dealCreation = useRootStoreSelector(
                state => state.dealCreation,
            );
            const { mutateAsync: createProject } =
                projectMutationHooks.useCreateProjectMutation();
            // Returns a promise that stays pending until the create settles.
            // FormSteps awaits onNext to hold its Finish button disabled for the
            // duration, which is what prevents a duplicate POST /api/projects/
            // (the backend 500s with "a deal with this name already exists").
            const confirmDeal = React.useCallback(
                async (databaseId: number) => {
                    const baseDeal = dealCreation.deal;
                    if (baseDeal === null) {
                        throw new Error("No deal info found");
                    }
                    try {
                        await createProject(
                            {
                                ...baseDeal,
                                databaseId,
                                properties: dealCreation.properties,
                            },
                            {
                                onSuccess: () => {
                                    Userpilot.track(
                                        "odin_user_completes_deal_creation",
                                    );
                                    navigate.toAppEntrypoint();
                                },
                            },
                        );
                    } catch {
                        // A failed create already shows an error toast (the
                        // mutation's onError), so there's nothing to do here. We
                        // only catch to keep the failure from bubbling up as an
                        // unhandled promise rejection. The caller awaits this to
                        // disable Finish while in flight and re-enables it once
                        // settled either way, so a failed create stays retryable.
                    }
                },
                [dealCreation, createProject],
            );
            const saveEditingProperty = useRootStoreSelector(
                state => state.dealCreationSaveEditingProperty,
            );
            const cancelEditingProperty = useRootStoreSelector(
                state => state.dealCreationCancelEditingProperty,
            );
            const editNewProperty = useRootStoreSelector(
                state => state.dealCreationEditNewProperty,
            );
            const editProperty = useRootStoreSelector(
                state => state.dealCreationEditPropertyAtIndex,
            );
            const updateEditingProperty = useRootStoreSelector(
                state => state.dealCreationUpdateEditingProperty,
            );
            const navigateToAppEntrypoint = navigate.toAppEntrypoint;
            const removePropertyFromDeal = useRootStoreSelector(
                state => state.dealCreationRemovePropertyAtIndex,
            );
            return {
                onMount,
                onUnmount,
                decrementStep: previousStep,
                nextStep,
                setDealNameCheck,
                setPropertyNameCheck,
                setPropertyAddressCheck,
                setDeal,
                confirmDeal,
                saveEditingProperty,
                cancelEditingProperty,
                editNewProperty,
                editProperty,
                updateEditingProperty,
                navigateToAppEntrypoint,
                removePropertyFromDeal,
            };
        },
        getCurrentDate,
    });

    const useReconciliationState = createUseReconciliationState({
        useRootStoreSelector,
        extractionQueryHooks,
    });
    const {
        ViewExtractionAnnualized,
        ViewExtractionAnnualizedOptions,
        ViewExtractionAnnualizedAdditionalOptions,
    } = createViewExtractionAnnualized({
        getCurrentDate,
        useState: ({ projectId, databaseId }) => {
            const annualizationType = useRootStoreSelector(
                state => state.annualizationType,
            );
            const {
                data: availablePeriods,
                isPending: periodsIsPending,
                isError: periodsIsError,
            } = extractionQueryHooks.useOperatingStatementPeriodsQuery({
                projectId,
                databaseId,
                annualizationType,
            });
            const loadAnnualizedExtractionPeriodsStatus = periodsIsPending
                ? "pending"
                : periodsIsError
                  ? "rejected"
                  : "resolved";
            const extractionAnnaualizedStartDate = useRootStoreSelector(
                state => state.extractionAnnaualizedStartDate,
            );
            const extractionAnnaualizedEndDate = useRootStoreSelector(
                state => state.extractionAnnaualizedEndDate,
            );
            const selectedAnnualizationGroup = useRootStoreSelector(
                state => state.selectedAnnualizationGroup,
            );
            const interval =
                annualizationType === "yearly_actual" ||
                annualizationType === "yearly_budget"
                    ? 12
                    : getInterval(selectedAnnualizationGroup);
            const {
                data: operatingStatementData,
                isPending: operatingStatementIsPending,
                isError: operatingStatementIsError,
            } = extractionQueryHooks.useOperatingStatementQuery({
                projectId,
                databaseId,
                startDate: extractionAnnaualizedStartDate,
                endDate: extractionAnnaualizedEndDate,
                interval,
                annualizationType,
            });
            const loadAnnualizedExtractionStatus =
                extractionAnnaualizedStartDate === null ||
                extractionAnnaualizedEndDate === null
                    ? "idle"
                    : operatingStatementIsPending
                      ? "pending"
                      : operatingStatementIsError
                        ? "rejected"
                        : "resolved";
            const extractionAnnualizedRowsLoaded =
                convertAPIResponseToDTOTableExtractionAnnualized(
                    operatingStatementData ?? null,
                    selectedAnnualizationGroup,
                );
            const { data: projectFilesData } =
                projectQueryHooks.useProjectFilesQuery(projectId, databaseId);
            const extractionsInfo = React.useMemo(
                () =>
                    projectFilesData?.transformedFileView
                        .filter(
                            (file): file is FileExtractionInfoWithParent =>
                                "parentFileName" in file &&
                                "extractionId" in file &&
                                file.parentFileName !== null,
                        )
                        .map(file => ({
                            fileName: file.parentFileName,
                            extractionId: file.extractionId,
                            sheetName: file.sheetName,
                            uploadedAt: file.uploadedAt,
                            uploadedBy: file.uploadedBy,
                            completedAt: file.completedAt,
                        })) ?? null,
                [projectFilesData],
            );
            const showAnnualizationSource = useRootStoreSelector(
                state => state.showAnnualizationSource,
            );
            const { data: userData } = userQueryHooks.useUserDataQuery();
            const isProdaUser = userData?.userInfo?.isProdaUser ?? false;
            const isE2EUser = userData?.userInfo?.isE2eUser ?? false;
            const reconciliationState = useReconciliationState({
                projectId,
                databaseId,
                annualizationType,
                startDate: extractionAnnaualizedStartDate,
                endDate: extractionAnnaualizedEndDate,
                selectedAnnualizationGroup,
            });
            return {
                loadAnnualizedExtractionStatus,
                loadAnnualizedExtractionPeriodsStatus,
                availablePeriods: availablePeriods ?? null,
                columnDefsOperatingStatements:
                    extractionAnnualizedRowsLoaded?.columnDefsOperatingStatements ||
                    null,
                rows: extractionAnnualizedRowsLoaded?.rows || null,
                nonOperatingRows:
                    extractionAnnualizedRowsLoaded?.nonOperatingRows || null,
                totalNetIncome:
                    extractionAnnualizedRowsLoaded?.totalNetIncome || null,
                extractionsInfo,
                extractionAnnaualizedStartDate,
                extractionAnnaualizedEndDate,
                selectedAnnualizationGroup,
                showSource: showAnnualizationSource,
                annualizationType,
                isProdaUser,
                isE2EUser,
                ...reconciliationState,
            };
        },
        useCases: () => {
            const resetAnnualizedExtractionState = useRootStoreSelector(
                state => state.resetAnnualizedExtractionState,
            );
            const addNotificationWithDefaultTimeout = useRootStoreSelector(
                state => state.addNotificationWithDefaultTimeout,
            );
            const onDateRangeChangeError = React.useCallback(
                (title: string) => {
                    addNotificationWithDefaultTimeout({
                        title,
                        kind: "toast",
                        severity: "danger",
                    });
                },
                [addNotificationWithDefaultTimeout],
            );
            const showAnnualizationExportModal = useRootStoreSelector(
                state => state.showAnnualizationExportModal,
            );
            const setSelectedAnnualizationGroup = useRootStoreSelector(
                state => state.setSelectedAnnualizationGroup,
            );
            const setExtractionAnnualizedDateRange = useRootStoreSelector(
                state => state.setExtractionAnnualizedDateRange,
            );
            const setShowAnnualizationSource = useRootStoreSelector(
                state => state.setShowAnnualizationSource,
            );
            const setAnnualizationType = useRootStoreSelector(
                state => state.setAnnualizationType,
            );
            const setShowReconciliation = useRootStoreSelector(
                state => state.setShowReconciliation,
            );
            const onChangeAnnualizationType = React.useCallback(
                ({ type }: { type: TAnnualizationType }) => {
                    setAnnualizationType(type);
                    setSelectedAnnualizationGroup("month");
                },
                [setAnnualizationType, setSelectedAnnualizationGroup],
            );
            return {
                onUnmount: resetAnnualizedExtractionState,
                onToggleReconciliation: setShowReconciliation,
                onDateRangeChange: React.useCallback(
                    ({
                        startDate,
                        endDate,
                    }: {
                        projectId: number;
                        startDate: string;
                        endDate: string;
                        databaseId: number;
                    }) => {
                        setExtractionAnnualizedDateRange({
                            startDate,
                            endDate,
                        });
                    },
                    [setExtractionAnnualizedDateRange],
                ),
                onDateRangeChangeError,
                onExportAnnualizationClicked: showAnnualizationExportModal,
                onChangeSelectedAnnualizationGroup: ({
                    group,
                }: {
                    projectId: number;
                    databaseId: number;
                    group: "month" | "quarter" | "half" | "year";
                }) => {
                    setSelectedAnnualizationGroup(group);
                },
                onToggleShowSource: setShowAnnualizationSource,
                onChangeAnnualizationType,
            };
        },
    });

    const ViewProjectDocuments = createViewProjectDocuments({
        useState: ({ projectId, databaseId }) => {
            const {
                data: filesData,
                isPending: projectFilesIsPending,
                isError: projectFilesIsError,
            } = projectQueryHooks.useProjectFilesQuery(projectId, databaseId);

            const { data: userData } = userQueryHooks.useUserDataQuery();
            const userType = userData?.userInfo?.userType;
            const userSelectedDatabases = userData?.userSelectedDatabases ?? [];
            const selectedExtractions = useRootStoreSelector(
                state => state.selectedExtractions,
            );
            const selectedDeletableExtractions = useRootStoreSelector(state =>
                state.getSelectedDeletableExtractions({
                    userType: userType ?? "analyst",
                }),
            );
            const selectedFiles = useRootStoreSelector(
                state => state.selectedFiles,
            );
            const selectedDeletableFiles = useRootStoreSelector(state =>
                state.getSelectedDeletableFiles({
                    userType: userType ?? "analyst",
                }),
            );
            const deleteModalOpen = useRootStoreSelector(
                state => state.deleteModalOpen,
            );
            return {
                deleteModalOpen,
                fileExtractionInfo: filesData?.transformedFileView ?? [],
                selectedExtractions,
                selectedDeletableExtractions,
                selectedFiles,
                selectedDeletableFiles,
                projectFilesIsPending,
                projectFilesIsError,
                userType,
                userSelectedDatabases,
            };
        },
        useFormattedDateState: () => {
            const { data: userData } = userQueryHooks.useUserDataQuery();
            return {
                userRegion: userData?.userRegion ?? "en-US",
            };
        },
        useCases: () => {
            const showExportModal = useRootStoreSelector(
                state => state.showExportModal,
            );
            const exportExtractions = ({
                extractions,
                projectId,
                databaseId,
            }: {
                extractions: {
                    extractionId: number;
                    extractionType: TExtractionType;
                }[];
                projectId: number;
                databaseId: number;
            }) =>
                showExportModal({
                    extractions,
                    projectId,
                    databaseId,
                });
            const downloadOriginalFilesMutation =
                projectMutationHooks.useDownloadOriginalFilesMutation();
            const downloadOriginalFiles = async ({
                fileIds,
                databaseId,
                projectId,
            }: {
                fileIds: number[];
                databaseId: number;
                projectId: number;
            }) => {
                try {
                    const responses =
                        await downloadOriginalFilesMutation.mutateAsync({
                            fileIds,
                            databaseId,
                            projectId,
                        });
                    for (const response of responses) {
                        const contentDispositionHeader =
                            response.response.headers.get(
                                "Content-Disposition",
                            );
                        let filename = "downloaded_file";
                        if (contentDispositionHeader !== null) {
                            const filenameFromDisposition =
                                contentDisposition.parse(
                                    contentDispositionHeader,
                                ).parameters["filename"];
                            if (filenameFromDisposition !== undefined) {
                                filename = filenameFromDisposition;
                            }
                        }
                        const blob = response.data as Blob;
                        downloadFile(blob, filename);
                    }
                    addNotificationWithDefaultTimeout({
                        title: "Files downloaded successfully",
                        kind: "toast",
                        severity: "success",
                    });
                } catch (error) {
                    addNotificationWithDefaultTimeout({
                        title: "Download failed",
                        kind: "toast",
                        severity: "danger",
                    });
                    console.error(error);
                }
            };
            const setDeleteModalOpenState = useRootStoreSelector(
                state => state.setDeleteModalOpenState,
            );
            const toggleExtraction = useRootStoreSelector(
                state => state.toggleExtraction,
            );
            const clearSelection = useRootStoreSelector(
                state => state.clearSelection,
            );
            const setSelectedFilesAndExtractions = useRootStoreSelector(
                state => state.setSelectedFilesAndExtractions,
            );
            const setFileAndChildren = useRootStoreSelector(
                state => state.setFileAndChildren,
            );
            const handleAddExtractionButtonClicked = ({
                projectId,
                fileId,
                activeTab,
                databaseId,
            }: {
                projectId: number;
                fileId: number;
                activeTab: TDealsOverviewTab;
                databaseId: number;
            }) => {
                navigate.toCreateExtraction({
                    projectId: projectId.toString(),
                    databaseId: databaseId.toString(),
                    fileId: fileId.toString(),
                    activeTab,
                });
            };
            const getSelectedStatus = useRootStoreSelector(
                state => state.getSelectedStatus,
            );
            const deleteFilesMutation =
                projectMutationHooks.useDeleteFilesMutation();
            const deleteExtractionsMutation =
                projectMutationHooks.useDeleteExtractionsMutation();

            return {
                setDeleteModalOpenState,
                deleteFiles: deleteFilesMutation.mutateAsync,
                deleteExtractions: deleteExtractionsMutation.mutateAsync,
                exportExtractions,
                downloadOriginalFiles,
                toggleExtraction,
                clearSelection,
                setSelectedFilesAndExtractions,
                getSelectedStatus,
                setFileAndChildren,
                handleAddExtractionButtonClicked,
            };
        },
    });

    const ViewDealOverview = createViewDealOverview({
        useState: ({ projectId, databaseId }) => {
            const {
                data,
                isPending: projectIsPending,
                isError: projectIsError,
            } = projectQueryHooks.useProjectDetailQuery(projectId, databaseId);

            return {
                projectPropertyInfo: data ?? null,
                projectIsPending,
                projectIsError,
            };
        },
        useCases: () => {
            const onMount = React.useCallback(
                ({
                    projectId,
                    databaseId,
                }: {
                    projectId: number;
                    databaseId: number;
                }) => {
                    return apiODIN.subscribeToDealUpdates({
                        projectId,
                        databaseId,
                        onFileUpdate: async () => {
                            await queryClient.invalidateQueries({
                                queryKey: projectKeys.all,
                            });
                        },
                        onPropertyUpdate: async ({ projectId, databaseId }) => {
                            await queryClient.invalidateQueries({
                                queryKey: projectKeys.detail(
                                    projectId,
                                    databaseId,
                                ),
                            });
                        },
                    });
                },
                [],
            );
            const { data: userData } = userQueryHooks.useUserDataQuery();
            const userSelectedDatabases = userData?.userSelectedDatabases ?? [];
            const availableDatabases = userData?.availableDatabases ?? [];
            const selectedDatabaseIdFromLoadedDatabases =
                getSelectedDatabasesFromLoadedDatabases({
                    userSelectedDatabases,
                    availableDatabases,
                });
            const selectedDatabaseIds = React.useMemo(
                () =>
                    selectedDatabaseIdFromLoadedDatabases.map(
                        database => database.databaseId,
                    ),
                [selectedDatabaseIdFromLoadedDatabases],
            );
            const getProdaProjectPageUrl = projectPageUrl({
                databaseIds: selectedDatabaseIds,
                currency: currencyFromURLParam,
            });

            return {
                onMount,
                getProdaProjectPageUrl,
            };
        },
        ViewExtractionAnnualized,
        ViewExtractionAnnualizedOptions,
        ViewExtractionAnnualizedAdditionalOptions,
        ViewProjectDocuments,
    });

    const ViewDealDetails = createViewDealDetails({
        useState: ({ projectId, databaseId }) => {
            const {
                data: currentDeal,
                isPending: dealIsPending,
                isError: dealIsError,
            } = projectQueryHooks.useMultipropDealQuery(projectId, databaseId);
            const currentProperty = useRootStoreSelector(
                state => state.multipropCurrentProperty,
            );
            const editingProperty = useRootStoreSelector(
                state => state.multipropEditingProperty,
            );
            const propertyAddressCheckMutation =
                projectMutationHooks.usePropertyAddressCheckMutation();
            const propertyAddressCheck =
                propertyAddressCheckMutation.data ?? null;
            const {
                data: googleMapsConfig,
                isPending: googleMapsIsPending,
                isError: googleMapsIsError,
            } = configQueryHooks.useGoogleMapsConfigQuery();
            const configGoogleMapsState = {
                status: googleMapsIsPending
                    ? ("pending" as const)
                    : googleMapsIsError
                      ? ("rejected" as const)
                      : ("resolved" as const),
                data: googleMapsConfig ?? null,
            };
            const {
                data: availableCoAsData,
                isPending: availableCoAsIsPending,
                isError: availableCoAsIsError,
            } = coaQueryHooks.useCoasByDatabaseQuery(databaseId);
            const availableCoAs = availableCoAsData ?? null;
            const availableCoAsStatus = availableCoAsIsPending
                ? "pending"
                : availableCoAsIsError
                  ? "rejected"
                  : "resolved";
            const addPropertyMutation =
                projectMutationHooks.useAddPropertyMutation();
            const deletePropertyModalOpen = useRootStoreSelector(
                state => state.deletePropertyModalOpen,
            );
            const propertyIdForDelete = useRootStoreSelector(
                state => state.propertyIdForDelete,
            );
            const { data: deleteInfoData, isPending: isDeleteInfoPending } =
                projectQueryHooks.usePropertyInfoForDeleteQuery(
                    propertyIdForDelete,
                    databaseId,
                    projectId,
                );
            const deleteInfo = deleteInfoData ?? null;
            const { data: dealFilesData } =
                projectQueryHooks.useProjectFilesQuery(projectId, databaseId);
            const projectHasCoaLockingExtractions = hasCoaLockingExtractions(
                dealFilesData?.filteredFiles ?? [],
            );
            return {
                currentDeal: currentDeal ?? null,
                dealIsPending,
                dealIsError,
                currentProperty,
                editingProperty,
                propertyAddressCheck,
                configGoogleMapsState,
                availableCoAs,
                availableCoAsStatus,
                isAddPropertyPending: addPropertyMutation.isPending,
                deletePropertyModalOpen,
                deleteInfo,
                isDeleteInfoPending,
                hasCoaLockingExtractions: projectHasCoaLockingExtractions,
            };
        },
        useCases: ({ projectId, databaseId }) => {
            const { data: currentDeal } =
                projectQueryHooks.useMultipropDealQuery(projectId, databaseId);
            const resetDealCreationState = useRootStoreSelector(
                state => state.resetDealCreationState,
            );
            const onMount = React.useCallback(() => {
                // CoA data is loaded via TanStack Query hook
            }, []);
            const onUnmount = React.useCallback(() => {
                resetDealCreationState();
            }, [resetDealCreationState]);

            const { mutateAsync: checkProjectName } =
                projectMutationHooks.useProjectNameCheckMutation();
            const setDealNameCheck = React.useCallback(
                async (databaseId: number, dealName: string) =>
                    await checkProjectName({
                        databaseId,
                        dealName,
                    }),
                [checkProjectName],
            );

            const { mutateAsync: checkPropertyAddress } =
                projectMutationHooks.usePropertyAddressCheckMutation();
            const setPropertyAddressCheck = React.useCallback(
                async (data: Parameters<typeof checkPropertyAddress>[0]) => {
                    return await checkPropertyAddress(data);
                },
                [checkPropertyAddress],
            );

            const { mutateAsync: checkPropertyName } =
                projectMutationHooks.usePropertyNameCheckMutation();
            const setPropertyNameCheck = React.useCallback(
                async (databaseId: number, propertyName: string) => {
                    const currentDealProperties = currentDeal?.properties ?? [];
                    const body = {
                        databaseId,
                        propertyName,
                        otherPropertyNames: currentDealProperties.map(
                            p => p.propertyName,
                        ),
                    };
                    return await checkPropertyName(body);
                },
                [checkPropertyName, currentDeal?.properties],
            );
            const updateProjectMutation =
                projectMutationHooks.useUpdateProjectMutation();
            const updateDeal = async (data: ProjectInfo) => {
                await updateProjectMutation.mutateAsync(data);
                addNotificationWithDefaultTimeout({
                    title: "Project details updated successfully",
                    severity: "success",
                    kind: "toast",
                });
                navigate.toPageDealOverviewDocuments({
                    projectId: data.projectId.toString(),
                    databaseId: data.databaseId.toString(),
                });
            };

            const addPropertyMutation =
                projectMutationHooks.useAddPropertyMutation();
            const addProperty = async (property: NewProperty) => {
                await addPropertyMutation.mutateAsync({
                    ...property,
                    projectId,
                    databaseId,
                    dealName: currentDeal?.dealName ?? "",
                });
            };

            const updatePropertyMutation =
                projectMutationHooks.useUpdatePropertyMutation();
            const updateProperty = async (property: MultipropPropertyInfo) => {
                await updatePropertyMutation.mutateAsync({
                    property,
                    projectId,
                    databaseId,
                });
            };

            const deletePropertyMutation =
                projectMutationHooks.useDeletePropertyMutation();
            const deleteProperty = async (propertyId: number) => {
                await deletePropertyMutation.mutateAsync({
                    propertyId,
                    databaseId,
                    projectId,
                });
            };

            const storeNewProperty = useRootStoreSelector(
                state => state.multipropNewProperty,
            );
            const newProperty = () => {
                storeNewProperty({ projectId, databaseId });
            };

            const storeEditProperty = useRootStoreSelector(
                state => state.multipropEditProperty,
            );
            const editProperty = (property: MultipropPropertyInfo) => {
                storeEditProperty(property);
            };

            const toggleEditingProperty = useRootStoreSelector(
                state => state.multipropToggleEditProperty,
            );
            const mutateNewProperty = useRootStoreSelector(
                state => state.multipropMutateNewProperty,
            );
            const setDeletePropertyModalStatus = useRootStoreSelector(
                state => state.setDeletePropertyModalStatus,
            );
            const setPropertyIdForDelete = useRootStoreSelector(
                state => state.setPropertyIdForDelete,
            );
            const handleDeletePropertyModalOpen = (propertyId: number) => {
                setPropertyIdForDelete(propertyId);
                setDeletePropertyModalStatus(true);
            };
            const handleDeletePropertyModalStatus = (open: boolean) => {
                if (!open) {
                    // Reset so reopening re-enables the info query and refetches
                    // — otherwise a failed load could never be retried.
                    setPropertyIdForDelete(null);
                }
                setDeletePropertyModalStatus(open);
            };
            return {
                onMount,
                onUnmount,
                setDealNameCheck,
                setPropertyAddressCheck,
                setPropertyNameCheck,
                updateDeal,
                addProperty,
                updateProperty,
                newProperty,
                editProperty,
                deleteProperty,
                toggleEditingProperty,
                mutateNewProperty,
                setDeletePropertyModalStatus: handleDeletePropertyModalStatus,
                handleDeletePropertyModalOpen,
            };
        },
        getCurrentDate,
    });

    const ViewDealActivities = createViewDealActivities({
        useState: ({ projectId, databaseId }) => {
            const { data: projectPropertyInfo } =
                projectQueryHooks.useProjectDetailQuery(projectId, databaseId);
            const { data: filesData } = projectQueryHooks.useProjectFilesQuery(
                projectId,
                databaseId,
            );
            const activityList = React.useMemo(
                () =>
                    getProjectActivityList({
                        projectPropertyInfo: projectPropertyInfo ?? null,
                        projectFilesWithDeletedExtractions:
                            filesData?.allFiles ?? [],
                    }),
                [projectPropertyInfo, filesData],
            );
            return {
                activityList,
            };
        },
        useFormattedDateState: () => {
            const { data: userData } = userQueryHooks.useUserDataQuery();
            return { userRegion: userData?.userRegion ?? null };
        },
    });

    const ViewToasts = createViewToasts({
        useState: () => ({
            toasts: useRootStoreSelector(state => state.selectToasts(state)),
        }),
        useCases: () => ({
            removeNotification: useRootStoreSelector(
                state => state.removeNotification,
            ),
        }),
    });

    const ViewExtractionSidebar = createViewExtractionsSidebar({
        useState: ({ extractionId, projectId, databaseId }) => {
            const { data: extractionData } =
                extractionQueryHooks.useExtractionQuery(extractionId);
            const { data: labelsetData } =
                extractionQueryHooks.useLabelsetQuery(extractionId);
            const extractionsFileName = extractionData?.raw.fileName ?? null;
            const rowsWithMappingsAltered = useRootStoreSelector(
                state => state.rowsWithMappingsAltered,
            );
            const rowsOmitted = useRootStoreSelector(
                state => state.rowsOmitted,
            );
            const { data: projectDetail } =
                projectQueryHooks.useProjectDetailQuery(
                    projectId ?? 0,
                    databaseId ?? 0,
                    {
                        enabled:
                            projectId !== undefined && databaseId !== undefined,
                    },
                );
            const coaId = projectDetail?.coaId ?? null;
            const { data: accountsData } = coaQueryHooks.useAccountsByCoaQuery(
                coaId,
                {
                    enabled: coaId !== null,
                },
            );
            const optionsAccountsWithType = getAccountOptionsWithTypes(
                accountsData ?? null,
            );
            const getErrors = useRootStoreSelector(state => state.getErrors);
            const errorGroups = getErrors()({
                rowsOmitted,
                rowsWithMappingsAltered,
                optionsAccounts: optionsAccountsWithType,
                llmValidation: extractionData?.raw.llmValidation ?? null,
                llmDiagnosis: extractionData?.raw.llmDiagnosis ?? null,
            });
            const highlightedCell = useRootStoreSelector(
                state => state.highlightedCell,
            );
            const extractionStatus = extractionData?.raw.status ?? null;
            const thereIsNoColumnSelectionInExtractionView = null;
            const labelsetExtractionInfo = labelsetData?.labelsetInfo ?? null;
            const labelsetSourceId = useRootStoreSelector(
                state => state.labelsetSourceId,
            );
            const selectedTable: TTableLocation | null = extractionData?.raw
                ? {
                      tableBounds: extractionData.raw.bounds,
                      worksheetName: extractionData.raw.worksheetName,
                      worksheetIndex: extractionData.raw.worksheetIndex,
                  }
                : null;
            return {
                extractionStatus,
                fileName: extractionsFileName,
                errorGroups,
                highlightedCell,
                selectedColumn: thereIsNoColumnSelectionInExtractionView,
                labelsetExtractionInfo,
                labelsetSourceId,
                showLabelset: true,
                selectedTable,
            };
        },
        useCases: ({ extractionId }) => {
            const { data: labelsetData } =
                extractionQueryHooks.useLabelsetQuery(extractionId);
            const setHighlightedCell = useRootStoreSelector(
                state => state.setHighlightedCell,
            );
            const setHighlightedTargetRowIds = useRootStoreSelector(
                state => state.setHighlightedTargetRowIds,
            );
            const storeOnDismissWarnings = useRootStoreSelector(
                state => state.onDismissWarnings,
            );
            const { mutate: dismissLlmDiagnoses } =
                extractionMutationHooks.useDismissLlmDiagnosesMutation();
            const { mutate: dismissOutliers } =
                extractionMutationHooks.useDismissOutliersMutation();
            const { mutateAsync: saveLabelsetMutateAsync } =
                extractionMutationHooks.useSaveLabelsetMutation();
            return {
                setHighlightedCell,
                setHighlightedTargetRowIds,
                onDismissWarnings: warnings => {
                    storeOnDismissWarnings(warnings);
                    dismissOutliers({
                        extractionId,
                        outliers: toDismissOutliersPayload(
                            warnings,
                            storeRoot.getState().rowsDismissed,
                        ),
                    });
                },
                onDismissDiagnosis: groups =>
                    dismissLlmDiagnoses({ extractionId, groups }),
                onLabelsetSourceChange: labelsetSourceId => {
                    const labelsetRowStates =
                        labelsetSourceId === null
                            ? {}
                            : labelsetData?.labelsetRowStates?.[
                                  labelsetSourceId
                              ] ?? {};
                    const labelsetEntries = Object.entries(labelsetRowStates);
                    const mappingRows = labelsetEntries
                        .filter(([, mapping]) => mapping.mappingType != null)
                        .map(([rowId, mapping]) => ({
                            rowId: parseInt(rowId, 10),
                            accountCode: mapping.account ?? null,
                        }));
                    const rowSemantics = labelsetEntries.flatMap(
                        ([rowId, mapping]) =>
                            mapping.rowType != null
                                ? [
                                      {
                                          rowId: parseInt(rowId, 10),
                                          rowType: mapping.rowType,
                                          flipRowSign:
                                              mapping.flipRowSign ?? false,
                                      },
                                  ]
                                : [],
                    );
                    // No optimistic local update: the mutation's onSuccess
                    // invalidates the extraction + labelset queries, and the
                    // refetch drives the grid and "Mapping Source Document"
                    // card to the new values in a single transition (covered by
                    // the loading overlay). This avoids the new->old->new
                    // flicker that an optimistic write would cause.
                    void saveLabelsetMutateAsync({
                        extractionId,
                        labelsetSourceId,
                        mappingRows,
                        rowSemantics,
                    });
                },
            };
        },
    });

    const ViewExtractionStandardizedSidebar = createViewExtractionsSidebar({
        useState: ({ extractionId, projectId, databaseId }) => {
            const { data: standardizedData } =
                extractionQueryHooks.useStandardizedExtractionQuery(
                    {
                        extractionId,
                        projectId: projectId ?? null,
                        databaseId: databaseId ?? null,
                    },
                    {
                        enabled:
                            projectId !== undefined && databaseId !== undefined,
                    },
                );
            const { data: projectFilesData } =
                projectQueryHooks.useProjectFilesQuery(
                    projectId ?? 0,
                    databaseId ?? 0,
                    {
                        enabled:
                            projectId !== undefined && databaseId !== undefined,
                    },
                );
            const extractionsFileName = standardizedData?.raw.fileName ?? null;
            const getOutliers = useRootStoreSelector(
                state => state.getOutliers,
            );
            const rowsDismissed = useRootStoreSelector(
                state => state.rowsDismissed,
            );
            const errorGroups = getOutliers()({
                rowsDismissed: rowsDismissed,
            });

            const highlightedCell = useRootStoreSelector(
                state => state.highlightedCell,
            );
            const extractionStatus = standardizedData?.raw.status ?? null;
            const extractionStandardizedActiveColumns = useRootStoreSelector(
                state => state.extractionStandardizedActiveColumns,
            );
            // Use the store's overlap-merged DTO (populated by
            // ViewExtractionReview), not the standardized query DTO. The query
            // DTO is built without overlapping periods, so its columnDefs lack
            // the per-extraction child columns that createSelectedColumnInfo
            // needs to resolve the selected column for draft overlaps.
            const extractionStandardizedRowsLoaded = useRootStoreSelector(
                state => state.extractionStandardizedRowsLoaded,
            );
            const extractionsInfo = React.useMemo(
                () =>
                    projectFilesData?.transformedFileView
                        .filter(
                            (file): file is FileExtractionInfoWithParent =>
                                "parentFileName" in file &&
                                "extractionId" in file &&
                                file.parentFileName !== null,
                        )
                        .map(file => ({
                            fileName: file.parentFileName,
                            extractionId: file.extractionId,
                            sheetName: file.sheetName,
                            uploadedAt: file.uploadedAt,
                            uploadedBy: file.uploadedBy,
                            completedAt: file.completedAt,
                        })) ?? null,
                [projectFilesData],
            );
            const selectedColumn = React.useMemo(
                () =>
                    createSelectedColumnInfo()({
                        extractionStatus,
                        highlightedCell,
                        extractionsInfo,
                        extractionStandardizedRowsLoaded,
                        extractionStandardizedActiveColumns,
                    }),
                [
                    extractionStandardizedActiveColumns,
                    extractionStandardizedRowsLoaded,
                    extractionStatus,
                    extractionsInfo,
                    highlightedCell,
                ],
            );

            return {
                extractionStatus,
                fileName: extractionsFileName,
                tables: null,
                errorGroups,
                highlightedCell,
                selectedColumn,
                showLabelset: false,
                labelsetExtractionInfo: null,
                labelsetSourceId: null,
                selectedTable: null,
            };
        },
        useCases: ({ extractionId }) => {
            const setHighlightedCell = useRootStoreSelector(
                state => state.setHighlightedCell,
            );
            const setHighlightedTargetRowIds = useRootStoreSelector(
                state => state.setHighlightedTargetRowIds,
            );
            const storeOnDismissWarnings = useRootStoreSelector(
                state => state.onDismissWarnings,
            );
            const { mutate: dismissOutliers } =
                extractionMutationHooks.useDismissOutliersMutation();
            const onLabelsetSourceChange = () => {};

            return {
                setHighlightedCell,
                setHighlightedTargetRowIds,
                onDismissWarnings: warnings => {
                    storeOnDismissWarnings(warnings);
                    dismissOutliers({
                        extractionId,
                        outliers: toDismissOutliersPayload(
                            warnings,
                            storeRoot.getState().rowsDismissed,
                        ),
                    });
                },
                onLabelsetSourceChange,
            };
        },
    });

    const ViewSettingsMenu = createViewSettingsMenu({
        useState: () => {
            const { isLoading, isAuthenticated, user } = useAuthState();
            const settingsOpen = useRootStoreSelector(
                state => state.settingsOpen,
            );
            const { data: userData } = userQueryHooks.useUserDataQuery();
            const isProdaUser = userData?.userInfo?.isProdaUser ?? false;
            const isAdmin = userData?.userInfo?.userType === "admin";
            const env = useRootStoreSelector(
                state => state.initialConfigState.data?.env ?? "unknown",
            );
            return {
                settingsOpen,
                isLoading,
                isAuthenticated,
                user: user || null,
                isProdaUser,
                isAdmin,
                env,
            };
        },
        useCases: () => {
            const toggleSettings = useRootStoreSelector(
                state => state.toggleSettings,
            );
            const logout = useRootStoreSelector(state => state.logout);
            const syncDatabasesMutation =
                settingsMutationHooks.useSyncDatabasesMutation();
            const dashboardButtonClicked = (tab: TDashboardTab) => {
                navigate.toDashboard(tab);
            };
            const chartsOfAccountsButtonClicked = (
                tab: TChartsOfAccountsTab,
            ) => {
                navigate.toChartsOfAccounts(tab);
            };
            return {
                handleSettingsClicked: toggleSettings,
                logout: () => void logout(),
                syncButtonClicked: async () => {
                    await syncDatabasesMutation.mutateAsync();
                },
                chartsOfAccountsButtonClicked,
                dashboardButtonClicked,
                userPoliciesButtonClicked: navigate.toUserPolicies,
            };
        },
    });

    const ViewTopBar = createViewTopBar({
        ViewDatabaseSelector,
        ViewBreadcrumbs,
    });

    const ViewNavigationSidebar = createViewNavigationSidebar({
        ViewSettingsMenu,
        useState: () => {
            const settingsOpen = useRootStoreSelector(
                state => state.settingsOpen,
            );
            const { data: userData } = userQueryHooks.useUserDataQuery();
            const isAdmin = userData?.userInfo?.userType === "admin";
            const isProdaUser = userData?.userInfo?.isProdaUser ?? false;
            const userSelectedDatabases = React.useMemo(
                () => userData?.userSelectedDatabases ?? [],
                [userData?.userSelectedDatabases],
            );
            const dataGovernanceState = useRootStoreSelector(
                state => state.dataGovernanceState,
            );
            const refreshDataGovernance = useRootStoreSelector(
                state => state.refreshDataGovernance,
            );
            const newUiEnabled = useRootStoreSelector<boolean>(
                state => state.newUiEnabled,
            );
            React.useEffect(() => {
                void refreshDataGovernance(userSelectedDatabases);
            }, [refreshDataGovernance, userSelectedDatabases]);

            return {
                settingsOpen,
                isProdaUser,
                isAdmin,
                userSelectedDatabases,
                hadDataGovernanceAccess:
                    dataGovernanceState.data?.hasAccess ?? false,
                dataGovernanceEnabledDbIds:
                    dataGovernanceState.data?.enabledDbIds ?? [],
                newUiEnabled,
            };
        },
    });

    const ViewNewExperienceSection = createViewNewExperienceSection({
        useState: () => {
            const { data: userData } = userQueryHooks.useUserDataQuery();
            const user = userData?.userInfo ?? null;
            const userSelectedDatabases = userData?.userSelectedDatabases ?? [];

            const newUiEnabled = useRootStoreSelector(
                state => state.newUiEnabled,
            );
            return {
                user,
                userSelectedDatabases,
                newUiEnabled,
            };
        },
        useCases: () => {
            const setNewUiEnabledStore = useRootStoreSelector(
                state => state.setNewUiEnabled,
            );
            const setNewUiEnabled = React.useCallback(
                (enabled: boolean): void => {
                    try {
                        window.localStorage.setItem(
                            "newUiEnabled",
                            String(enabled),
                        );
                    } catch {
                        // ignore localStorage errors; store still updates
                    }
                    setNewUiEnabledStore(enabled);
                },
                [setNewUiEnabledStore],
            );

            return { setNewUiEnabled };
        },
        createProdaLink: createProdaLinks,
        createCoreUiLink: createCoreUILinks,
    });

    const ViewDatabaseGuard = createViewDatabaseGuard({
        useState: () => {
            const {
                data: userData,
                isPending,
                isError,
                isSuccess,
            } = userQueryHooks.useUserDataQuery();
            const availableDatabases = userData?.availableDatabases ?? [];
            const userSelectedDatabases = userData?.userSelectedDatabases ?? [];
            const selectedDatabaseIdFromLoadedDatabases =
                getSelectedDatabasesFromLoadedDatabases({
                    userSelectedDatabases,
                    availableDatabases,
                });
            return {
                databaseLoadStatus: isPending
                    ? "pending"
                    : isError
                      ? "rejected"
                      : "resolved",
                initialLoadingSuccess: isSuccess,
                databasesCount: availableDatabases.length,
                selectedDatabaseIds: selectedDatabaseIdFromLoadedDatabases.map(
                    database => database.databaseId,
                ),
            };
        },
    });

    const ViewSelectDatabaseModal = createViewSelectDatabaseModal({
        useState: () => {
            const showModal = useRootStoreSelector(
                state => state.showDatabaseSelectionModal,
            );
            const selectedDatabase = useRootStoreSelector(
                state => state.dealCreationDatabaseId,
            );
            const { data: userData } = userQueryHooks.useUserDataQuery();
            const userSelectedDatabases = userData?.userSelectedDatabases ?? [];
            const availableDatabases = userData?.availableDatabases ?? [];
            const selectedDatabaseIdFromLoadedDatabases =
                getSelectedDatabasesFromLoadedDatabases({
                    userSelectedDatabases,
                    availableDatabases,
                });
            return {
                showModal,
                availableDatabases: selectedDatabaseIdFromLoadedDatabases,
                selectedDatabase: selectedDatabase ?? -1,
            };
        },
        useCases: () => {
            const setShowDatabaseSelectionModal = useRootStoreSelector(
                state => state.setShowDatabaseSelectionModal,
            );
            const onCancel = () => setShowDatabaseSelectionModal(false);
            const selectedDatabaseId = useRootStoreSelector(
                state => state.dealCreationDatabaseId,
            );
            const onConfirm = () => {
                setShowDatabaseSelectionModal(false);
                navigate.toDealCreation(selectedDatabaseId?.toString() ?? "");
            };
            const onSelectDatabase = useRootStoreSelector(
                state => state.setDealCreationDatabaseId,
            );
            return {
                onConfirm,
                onCancel,
                onSelectDatabase,
            };
        },
    });

    const ViewUserConsentModal = createViewUserConsentModal({
        useState: () => {
            const { data: userData } = userQueryHooks.useUserDataQuery();
            const showModal = userData?.userConsent.userConsentValid === false;
            const userConsentChecked = useRootStoreSelector(
                state => state.userConsentChecked,
            );
            return { showModal, userConsentChecked };
        },
        useCases: () => {
            const submitUserConsentMutation =
                userMutationHooks.useSubmitUserConsentMutation();
            const setUserConsentChecked = useRootStoreSelector(
                state => state.setUserConsentChecked,
            );
            const onConfirm = React.useCallback(async () => {
                await submitUserConsentMutation.mutateAsync(true);
            }, [submitUserConsentMutation]);

            return {
                setUserConsentChecked,
                onConfirm,
            };
        },
    });

    const ViewAuthCallback = createViewAuthCallback({
        useState: useAuthState,
        useCases: () => {
            const handleAuthRedirect = useRootStoreSelector(
                state => state.handleAuthRedirect,
            );
            const onMount = React.useCallback(() => {
                void handleAuthRedirect();
            }, [handleAuthRedirect]);
            return {
                onMount,
            };
        },
    });

    const ViewAuthGuard = createViewAuthGuard({
        useState: useAuthState,
        useCases: () => {
            const checkIfUserIsAuthenticatedOrRedirectToLogin =
                useRootStoreSelector(
                    state => state.checkIfUserIsAuthenticatedOrRedirectToLogin,
                );
            const onMount = React.useCallback(
                (returnTo: string) => {
                    void checkIfUserIsAuthenticatedOrRedirectToLogin({
                        returnTo,
                    });
                },
                [checkIfUserIsAuthenticatedOrRedirectToLogin],
            );
            return {
                onMount,
            };
        },
    });

    const ViewThirdPartyDoc = createViewThirdPartyDoc({
        useState: () => {
            const isThirdPartyAuthenticated = useRootStoreSelector(
                state => state.isThirdPartyAuthenticated,
            );
            const thirdPartyTokenAvailableState = useRootStoreSelector(
                state => state.thirdPartyTokenAvailableState,
            );
            const isAuthConfigLoading = useRootStoreSelector(
                state => state.thirdPartyAuthConfig.status !== "resolved",
            );
            const isLoading =
                thirdPartyTokenAvailableState === "pending" ||
                thirdPartyTokenAvailableState === "rejected" ||
                isAuthConfigLoading ||
                thirdPartyTokenAvailableState === "idle";
            const dealListRequestDatabaseIds = useRootStoreSelector(
                state => state.dealListRequestDatabaseIds,
            );
            const dealPeriodsRequestDealId = useRootStoreSelector(
                state => state.dealPeriodsRequestDealId,
            );
            const operatingStatementRequestDealId = useRootStoreSelector(
                state => state.operatingStatementRequestDealId,
            );
            const token = useRootStoreSelector(state => state.accessToken);
            const databaseListResponse = useRootStoreSelector(
                state => state.databaseListResponse,
            );
            const dealListResponse = useRootStoreSelector(
                state => state.dealListResponse,
            );
            const getOperatingStatementByIdResponse = useRootStoreSelector(
                state => state.getOperatingStatementByIdResponse,
            );
            const getOperatingStatementByNameResponse = useRootStoreSelector(
                state => state.getOperatingStatementByNameResponse,
            );
            const dealPeriodsResponse = useRootStoreSelector(
                state => state.dealPeriodsResponse,
            );
            const operatingStatementDetailed = useRootStoreSelector(
                state => state.operatingStatementDetailed,
            );
            const operatingStatementRequestFromDate = useRootStoreSelector(
                state => state.operatingStatementRequestFromDate,
            );
            const operatingStatementRequestToDate = useRootStoreSelector(
                state => state.operatingStatementRequestToDate,
            );
            const operatingStatementRequestAggregationInterval =
                useRootStoreSelector(
                    state => state.operatingStatementRequestAggregationInterval,
                );
            const operatingStatementLookupType = useRootStoreSelector(
                state => state.operatingStatementLookupType,
            );
            const operatingStatementLookupValue = useRootStoreSelector(
                state => state.operatingStatementLookupValue,
            );
            const coaListResponse = useRootStoreSelector(
                state => state.coaListResponse,
            );
            const coaDetailResponse = useRootStoreSelector(
                state => state.coaDetailResponse,
            );
            const coaListRequestDatabaseIds = useRootStoreSelector(
                state => state.coaListRequestDatabaseIds,
            );
            const coaDetailRequestCoaId = useRootStoreSelector(
                state => state.coaDetailRequestCoaId,
            );
            const odinURL = getBaseHttpUrl();
            const openApiDocUrl = `${odinURL}${routerPaths.openApiDoc.default()}`;
            const tokenVisible = useRootStoreSelector(
                state => state.tokenVisible,
            );
            const projectPeriodsRequestProjectId = useRootStoreSelector(
                state => state.projectPeriodsRequestProjectId,
            );
            const operatingStatementRequestProjectId = useRootStoreSelector(
                state => state.projectOperatingStatementRequestProjectId,
            );
            const projectOperatingStatementResponse = useRootStoreSelector(
                state => state.projectOperatingStatementResponse,
            );
            const projectPeriodsResponse = useRootStoreSelector(
                state => state.projectPeriodsResponse,
            );
            const projectOperatingStatementDetailed = useRootStoreSelector(
                state => state.projectOperatingStatementDetailed,
            );
            const projectOperatingStatementRequestFromDate =
                useRootStoreSelector(
                    state => state.projectOperatingStatementRequestFromDate,
                );
            const projectOperatingStatementRequestToDate = useRootStoreSelector(
                state => state.projectOperatingStatementRequestToDate,
            );
            const projectOperatingStatementRequestAggregationInterval =
                useRootStoreSelector(
                    state =>
                        state.projectOperatingStatementRequestAggregationInterval,
                );
            return {
                isThirdPartyAuthenticated,
                isLoading,
                dealListRequestDatabaseIds,
                dealPeriodsRequestDealId,
                operatingStatementRequestDealId,
                token,
                tokenVisible,
                databaseListResponse: databaseListResponse.data,
                databaseListRequestError: databaseListResponse.error,
                dealListResponse: dealListResponse.data,
                dealListRequestError: dealListResponse.error,
                getOperatingStatementByIdResponse:
                    getOperatingStatementByIdResponse.data,
                getOperatingStatementByNameResponse:
                    getOperatingStatementByNameResponse.data,
                getOperatingStatementByIdRequestError:
                    getOperatingStatementByIdResponse.error,
                getOperatingStatementByNameRequestError:
                    getOperatingStatementByNameResponse.error,
                databaseListResponseStatus: databaseListResponse.status,
                dealListResponseStatus: dealListResponse.status,
                getOperatingStatementByIdResponseStatus:
                    getOperatingStatementByIdResponse.status,
                getOperatingStatementByNameResponseStatus:
                    getOperatingStatementByNameResponse.status,
                odinURL,
                openApiDocUrl,
                dealPeriodsResponse: dealPeriodsResponse.data,
                dealPeriodsRequestError: dealPeriodsResponse.error,
                dealPeriodsResponseStatus: dealPeriodsResponse.status,
                operatingStatementDetailed,
                operatingStatementRequestFromDate,
                operatingStatementRequestToDate,
                operatingStatementRequestAggregationInterval,
                projectPeriodsRequestProjectId,
                operatingStatementRequestProjectId,
                projectOperatingStatementResponse:
                    projectOperatingStatementResponse.data,
                projectOperatingStatementResponseStatus:
                    projectOperatingStatementResponse.status,
                projectPeriodsResponse: projectPeriodsResponse.data,
                projectPeriodsResponseStatus: projectPeriodsResponse.status,
                projectOperatingStatementDetailed,
                projectOperatingStatementRequestFromDate,
                projectOperatingStatementRequestToDate,
                projectOperatingStatementRequestAggregationInterval,
                projectPeriodsRequestError: projectPeriodsResponse.error,
                projectOperatingStatementRequestError:
                    projectOperatingStatementResponse.error,
                operatingStatementLookupType,
                operatingStatementLookupValue,
                coaListResponse: coaListResponse.data,
                coaListRequestError: coaListResponse.error,
                coaListResponseStatus: coaListResponse.status,
                coaDetailResponse: coaDetailResponse.data,
                coaDetailRequestError: coaDetailResponse.error,
                coaDetailResponseStatus: coaDetailResponse.status,
                coaListRequestDatabaseIds,
                coaDetailRequestCoaId,
                isNonProductionEnvironment: !isProductionEnvironment(),
            };
        },
        useCases: () => {
            const setDealListRequestDatabaseIds = useRootStoreSelector(
                state => state.setDealListRequestDatabaseIds,
            );
            const setDealPeriodsRequestDealId = useRootStoreSelector(
                state => state.setDealPeriodsRequestDealId,
            );
            const setProjectPeriodsRequestProjectId = useRootStoreSelector(
                state => state.setProjectPeriodsRequestProjectId,
            );
            const setOperatingStatementRequestDealId = useRootStoreSelector(
                state => state.setOperatingStatementRequestDealId,
            );
            const setOperatingStatementRequestProjectId = useRootStoreSelector(
                state => state.setProjectOperatingStatementRequestProjectId,
            );
            const handleLoginButtonClicked = useRootStoreSelector(
                state => state.handleLoginButtonClicked,
            );
            const handleLogoutButtonClicked = useRootStoreSelector(
                state => state.handleLogoutButtonClicked,
            );
            const handleRequestDatabases = useRootStoreSelector(
                state => state.handleRequestDatabases,
            );
            const handleRequestDeals = useRootStoreSelector(
                state => state.handleRequestDeals,
            );
            const handleRequestDealPeriods = useRootStoreSelector(
                state => state.handleRequestDealPeriods,
            );
            const handleRequestProjectPeriods = useRootStoreSelector(
                state => state.handleRequestProjectPeriods,
            );
            const handleRequestOperatingStatementById = useRootStoreSelector(
                state => state.handleRequestOperatingStatementById,
            );
            const handleRequestOperatingStatementByName = useRootStoreSelector(
                state => state.handleRequestOperatingStatementByName,
            );
            const handleRequestProjectOperatingStatement = useRootStoreSelector(
                state => state.handleRequestProjectOperatingStatement,
            );
            const toggleToken = useRootStoreSelector(
                state => state.toggleToken,
            );
            const copyToken = useRootStoreSelector(state => state.copyToken);

            const resetDatabaseListResponse = useRootStoreSelector(
                state => state.resetDatabaseListResponse,
            );
            const resetDealListResponse = useRootStoreSelector(
                state => state.resetDealListResponse,
            );
            const resetDealPeriodsResponse = useRootStoreSelector(
                state => state.resetDealPeriodsResponse,
            );
            const resetProjectPeriodsResponse = useRootStoreSelector(
                state => state.resetProjectPeriodsResponse,
            );
            const resetOperatingStatementByIdResponse = useRootStoreSelector(
                state => state.resetOperatingStatementByIdResponse,
            );
            const resetOperatingStatementByNameResponse = useRootStoreSelector(
                state => state.resetOperatingStatementByNameResponse,
            );
            const resetProjectOperatingStatementResponse = useRootStoreSelector(
                state => state.resetProjectOperatingStatementResponse,
            );
            const setOperatingStatementDetailed = useRootStoreSelector(
                state => state.setOperatingStatementDetailed,
            );
            const setProjectOperatingStatementDetailed = useRootStoreSelector(
                state => state.setProjectOperatingStatementDetailed,
            );
            const setOperatingStatementRequestFromDate = useRootStoreSelector(
                state => state.setOperatingStatementRequestFromDate,
            );
            const setProjectOperatingStatementRequestFromDate =
                useRootStoreSelector(
                    state => state.setProjectOperatingStatementRequestFromDate,
                );
            const setOperatingStatementRequestToDate = useRootStoreSelector(
                state => state.setOperatingStatementRequestToDate,
            );
            const setProjectOperatingStatementRequestToDate =
                useRootStoreSelector(
                    state => state.setProjectOperatingStatementRequestToDate,
                );
            const setOperatingStatementRequestAggregationInterval =
                useRootStoreSelector(
                    state =>
                        state.setOperatingStatementRequestAggregationInterval,
                );
            const setOperatingStatementLookupType = useRootStoreSelector(
                state => state.setOperatingStatementLookupType,
            );
            const setOperatingStatementLookupValue = useRootStoreSelector(
                state => state.setOperatingStatementLookupValue,
            );
            const handleRequestOperatingStatementByLookup =
                useRootStoreSelector(
                    state => state.handleRequestOperatingStatementByLookup,
                );
            const setProjectOperatingStatementRequestAggregationInterval =
                useRootStoreSelector(
                    state =>
                        state.setProjectOperatingStatementRequestAggregationInterval,
                );
            const handleRequestChartOfAccountsList = useRootStoreSelector(
                state => state.handleRequestChartOfAccountsList,
            );
            const handleRequestChartOfAccountsById = useRootStoreSelector(
                state => state.handleRequestChartOfAccountsById,
            );
            const setCoaListRequestDatabaseIds = useRootStoreSelector(
                state => state.setCoaListRequestDatabaseIds,
            );
            const setCoaDetailRequestCoaId = useRootStoreSelector(
                state => state.setCoaDetailRequestCoaId,
            );
            const resetCoaListResponse = useRootStoreSelector(
                state => state.resetCoaListResponse,
            );
            const resetCoaDetailResponse = useRootStoreSelector(
                state => state.resetCoaDetailResponse,
            );
            const initialiseThirdPartyAuth = useRootStoreSelector(
                state => state.initialiseThirdPartyAuth,
            );
            const handleAuthRedirectAndFetchUser = useRootStoreSelector(
                state => state.handleAuthRedirectAndFetchUser,
            );

            const ensureTokenIsAvaiable = useRootStoreSelector(
                state => state.ensureTokenIsAvaiable,
            );
            const onMount = React.useCallback(async () => {
                await initialiseThirdPartyAuth();
                await handleAuthRedirectAndFetchUser();
                await ensureTokenIsAvaiable();
            }, [
                ensureTokenIsAvaiable,
                initialiseThirdPartyAuth,
                handleAuthRedirectAndFetchUser,
            ]);
            return {
                onMount,
                handleLoginButtonClicked,
                handleLogoutButtonClicked,
                setDealListRequestDatabaseIds,
                setDealPeriodsRequestDealId,
                setProjectPeriodsRequestProjectId,
                setOperatingStatementRequestDealId,
                setOperatingStatementRequestProjectId,
                handleRequestDatabases,
                handleRequestDeals,
                handleRequestDealPeriods,
                handleRequestProjectPeriods,
                handleRequestOperatingStatementById,
                handleRequestOperatingStatementByName,
                handleRequestProjectOperatingStatement,
                toggleToken,
                copyToken,
                resetDatabaseListResponse,
                resetDealListResponse,
                resetDealPeriodsResponse,
                resetProjectPeriodsResponse,
                resetOperatingStatementByIdResponse,
                resetOperatingStatementByNameResponse,
                resetProjectOperatingStatementResponse,
                setOperatingStatementDetailed,
                setProjectOperatingStatementDetailed,
                setOperatingStatementRequestFromDate,
                setProjectOperatingStatementRequestFromDate,
                setOperatingStatementRequestToDate,
                setProjectOperatingStatementRequestToDate,
                setOperatingStatementRequestAggregationInterval,
                setProjectOperatingStatementRequestAggregationInterval,
                setOperatingStatementLookupType,
                setOperatingStatementLookupValue,
                handleRequestOperatingStatementByLookup,
                handleRequestChartOfAccountsList,
                handleRequestChartOfAccountsById,
                setCoaListRequestDatabaseIds,
                setCoaDetailRequestCoaId,
                resetCoaListResponse,
                resetCoaDetailResponse,
            };
        },
    });

    const {
        ViewConcurrentUsersIndicator,
        ViewConcurrentUsersBanner,
        HeadlessViewConcurrentUsersStartSessionOnMountAndStopOnUnmount,
    } = createViewConcurrentUsers({
        useState: () => {
            const concurrentUsers = useRootStoreSelector(
                state => state.concurrentUsers,
            );
            const showBanner = useRootStoreSelector(
                state => state.showConcurrentUserBanner,
            );

            return {
                concurrentUsers,
                showBanner,
            };
        },
        useCases: () => {
            const dismissBanner = useRootStoreSelector(
                state => state.dismissConcurrentUserBanner,
            );
            const startScreenSession = useRootStoreSelector(
                state => state.startScreenSession,
            );
            const cleanUpExtractionScreenSession = useRootStoreSelector(
                state => state.cleanUpExtractionScreenSession,
            );
            const startListeningForConcurrentUsers = React.useCallback(
                ({
                    extractionId,
                    resourceKey,
                }: {
                    extractionId: number;
                    resourceKey: "extraction-mapping" | "coa-remapping";
                }) => {
                    void startScreenSession({ extractionId, resourceKey });
                },
                [startScreenSession],
            );
            const stopListeningForConcurrentUsers = React.useCallback(
                cleanUpExtractionScreenSession,
                [cleanUpExtractionScreenSession],
            );

            return {
                dismissBanner,
                startListeningForConcurrentUsers,
                stopListeningForConcurrentUsers,
            };
        },
    });

    const ViewExtraction = createViewExtractionMappings({
        useState: ({ extractionId, projectId, databaseId }) => {
            const {
                data: extractionData,
                isLoading: extractionIsLoading,
                isError: extractionIsError,
            } = extractionQueryHooks.useExtractionQuery(extractionId);
            const { data: userData } = userQueryHooks.useUserDataQuery();
            const { data: labelsetData } =
                extractionQueryHooks.useLabelsetQuery(extractionId);
            const { data: extractionTypeData } =
                extractionQueryHooks.useExtractionTypeQuery(extractionId);
            const {
                data: projectDetail,
                isLoading: projectDetailIsLoading,
                isError: projectDetailIsError,
            } = projectQueryHooks.useProjectDetailQuery(
                projectId ?? 0,
                databaseId ?? 0,
                {
                    enabled:
                        projectId !== undefined && databaseId !== undefined,
                },
            );
            const coaId = projectDetail?.coaId ?? null;
            const {
                data: accountsData,
                isLoading: accountsIsLoading,
                isError: accountsIsError,
            } = coaQueryHooks.useAccountsByCoaQuery(coaId, {
                enabled: coaId !== null,
            });
            const updateExtractionRowsLoaded = useRootStoreSelector(
                state => state.updateExtractionRowsLoaded,
            );
            const setRowsAlreadyAltered = useRootStoreSelector(
                state => state.setRowsAlreadyAltered,
            );
            const setRowsAlreadyOmitted = useRootStoreSelector(
                state => state.setRowsAlreadyOmitted,
            );
            const setLabelsetSourceId = useRootStoreSelector(
                state => state.setLabelsetSourceId,
            );
            React.useEffect(() => {
                if (!extractionData) return;
                updateExtractionRowsLoaded({
                    updatedRows: extractionData.dto,
                });
                setRowsAlreadyAltered({
                    rows: extractionData.dto.rowsWithMappingsAltered,
                });
                setRowsAlreadyOmitted({
                    rows: extractionData.dto.rowsOmitted,
                });
            }, [
                extractionData,
                setRowsAlreadyAltered,
                setRowsAlreadyOmitted,
                updateExtractionRowsLoaded,
            ]);
            React.useEffect(() => {
                if (labelsetData === undefined) return;
                setLabelsetSourceId({
                    labelsetSourceId:
                        labelsetData.selectedLabelsetSourceId ?? null,
                });
            }, [labelsetData, setLabelsetSourceId]);
            const isAccountsLoading =
                projectDetailIsLoading || (coaId !== null && accountsIsLoading);
            const isAccountsError =
                projectDetailIsError ||
                (projectDetail !== undefined && coaId === null) ||
                accountsIsError;
            const isAccountsReady = accountsData !== undefined;
            const isExtractionReady = extractionData !== undefined;
            const saveMappingsMutationsInflight = useMutationState({
                filters: {
                    mutationKey:
                        extractionMutationHooks.extractionMutationKeys
                            .saveMappings,
                    status: "pending",
                },
            }).length;
            const saveLabelsetMutationsInflight = useMutationState({
                filters: {
                    mutationKey:
                        extractionMutationHooks.extractionMutationKeys
                            .saveLabelset,
                    status: "pending",
                },
            }).length;
            const changeTableMutationStatuses = useMutationState({
                filters: {
                    mutationKey:
                        extractionMutationHooks.extractionMutationKeys
                            .changeTable,
                },
                select: mutation => mutation.state.status,
            });
            const isTableChangePending = changeTableMutationStatuses.some(
                status => status === "pending",
            );
            const isTableChangeError =
                !isTableChangePending &&
                changeTableMutationStatuses.at(-1) === "error";
            const optionsAccountsWithType = React.useMemo(
                () => getAccountOptionsWithTypes(accountsData ?? null),
                [accountsData],
            );
            const rowsWithMappingsAltered = useRootStoreSelector(
                state => state.rowsWithMappingsAltered,
            );
            const rowsOmitted = useRootStoreSelector(
                state => state.rowsOmitted,
            );
            const rowsExtracted = useRootStoreSelector(
                state => state.extractionRowsLoaded ?? null,
            );
            const extractionStatus = extractionData?.raw.status ?? null;
            const dealName = extractionData?.raw.dealName ?? null;
            const getErrors = useRootStoreSelector(state => state.getErrors);
            const getTableData = useRootStoreSelector(
                state => state.getTableData,
            );
            const closeExtractionModalOpen = useRootStoreSelector(
                state => state.closeExtractionModalOpen,
            );
            const extractionType = extractionTypeData ?? null;
            const changeRowTypeMutationsInflight = useMutationState({
                filters: {
                    mutationKey:
                        extractionMutationHooks.extractionMutationKeys
                            .changeRowType,
                    status: "pending",
                },
            }).length;
            const flipRowSignMutationsInflight = useMutationState({
                filters: {
                    mutationKey:
                        extractionMutationHooks.extractionMutationKeys
                            .flipRowSign,
                    status: "pending",
                },
            }).length;
            const resetRowChangesMutationsInflight = useMutationState({
                filters: {
                    mutationKey:
                        extractionMutationHooks.extractionMutationKeys
                            .resetRowChanges,
                    status: "pending",
                },
            }).length;
            const editCellValuesMutationsInflight = useMutationState({
                filters: {
                    mutationKey:
                        extractionMutationHooks.extractionMutationKeys
                            .editCellValues,
                    status: "pending",
                },
            }).length;
            const revertCellValuesMutationsInflight = useMutationState({
                filters: {
                    mutationKey:
                        extractionMutationHooks.extractionMutationKeys
                            .revertCellValues,
                    status: "pending",
                },
            }).length;

            const editMode = useRootStoreSelector(state => state.editMode);

            const {
                rows: rowData,
                columnDefsOperatingStatements,
                totalRows,
                categoryRows,
            } = React.useMemo(
                () =>
                    // Recompute when extracted cell values change.
                    (rowsExtracted &&
                        getTableData()({
                            rowsWithMappingsAltered,
                            rowsOmitted,
                            optionsAccounts: optionsAccountsWithType,
                        })) || {
                        rows: null,
                        totalRows: null,
                        columnDefsOperatingStatements: null,
                        categoryRows: null,
                    },
                [
                    getTableData,
                    rowsExtracted,
                    rowsWithMappingsAltered,
                    rowsOmitted,
                    optionsAccountsWithType,
                ],
            );

            const totalNetIncomeOriginal =
                rowsExtracted?.documentTargetRow ?? null;
            const totalNetIncomeAltered = React.useMemo(
                () =>
                    rowData &&
                    createSelectorTableMappingsTotalNetIncome()({
                        rows: rowData,
                        optionsAccounts: optionsAccountsWithType.map(
                            option => ({
                                accountType: option.type,
                                accountCode: option.value,
                            }),
                        ),
                        isOriginalTotalNetIncome: false,
                    }),
                [rowData, optionsAccountsWithType],
            );

            const errorGroups = React.useMemo(
                () =>
                    getErrors()({
                        rowsOmitted,
                        rowsWithMappingsAltered,
                        optionsAccounts: optionsAccountsWithType,
                        llmValidation:
                            extractionData?.raw.llmValidation ?? null,
                        llmDiagnosis: extractionData?.raw.llmDiagnosis ?? null,
                    }),
                [
                    getErrors,
                    rowsOmitted,
                    rowsWithMappingsAltered,
                    optionsAccountsWithType,
                    extractionData?.raw.llmValidation,
                    extractionData?.raw.llmDiagnosis,
                ],
            );

            const alteredRowsIds = React.useMemo(
                () =>
                    rowsWithMappingsAltered
                        .filter(row => row.reverted === false)
                        .map(row => row.rowId),
                [rowsWithMappingsAltered],
            );

            const thereAreNotResolvedErrors = React.useMemo(
                () =>
                    Object.values(errorGroups).some(group =>
                        group.some(
                            error =>
                                error.severity === "error" &&
                                error.resolved !== true &&
                                error.errorType !== "llmValidation",
                        ),
                    ),
                [errorGroups],
            );
            const showLoadingOverlay =
                saveMappingsMutationsInflight > 0 ||
                saveLabelsetMutationsInflight > 0 ||
                changeRowTypeMutationsInflight > 0 ||
                flipRowSignMutationsInflight > 0 ||
                resetRowChangesMutationsInflight > 0 ||
                editCellValuesMutationsInflight > 0 ||
                revertCellValuesMutationsInflight > 0;
            const disableNextButton =
                showLoadingOverlay || thereAreNotResolvedErrors;
            const selectedRows = useRootStoreSelector(
                state => state.selectedRows,
            );
            const selectedColumns = useRootStoreSelector(
                state => state.selectedColumns,
            );

            const allFoundTables = extractionData?.raw.allFoundTables ?? null;
            const setColumnStateMutationsInflight = useMutationState({
                filters: {
                    mutationKey:
                        extractionMutationHooks.extractionMutationKeys
                            .setColumnState,
                    status: "pending",
                },
            }).length;
            const batchSetColumnStateMutationsInflight = useMutationState({
                filters: {
                    mutationKey:
                        extractionMutationHooks.extractionMutationKeys
                            .batchSetColumnState,
                    status: "pending",
                },
            }).length;
            const isColumnStatePending =
                setColumnStateMutationsInflight +
                    batchSetColumnStateMutationsInflight >
                0;

            return {
                showLoadingOverlay,
                disableNextButton,
                isAccountsLoading,
                isAccountsError,
                isAccountsReady,
                isExtractionLoading: extractionIsLoading,
                isExtractionError: extractionIsError,
                isExtractionReady,
                isTableChangePending,
                isTableChangeError,
                extractionStatus,
                rowData,
                totalRows,
                categoryRows,
                optionsAccountsWithType,
                dealName,
                alteredRowsIds,
                totalNetIncomeOriginal,
                totalNetIncomeAltered,
                columnDefsOperatingStatements,
                errorGroups,
                closeExtractionModalOpen,
                extractionType,
                selectedRows,
                selectedColumns,
                editMode,
                allFoundTables,
                isColumnStatePending,
                fileName: extractionData?.raw.fileName ?? null,
                isAiExtractionEnabled: userData?.aiFeaturesEnabled ?? false,
                isFromLlm: extractionData?.raw.fromLlm ?? false,
                highlightedTargetRowIds: useRootStoreSelector(
                    state => state.highlightedTargetRowIds,
                ),
            };
        },
        useCases: () => {
            const changeMapping = useRootStoreSelector(
                state => state.changeMapping,
            );
            const omitRow = useRootStoreSelector(state => state.omitRow);
            const { mutateAsync: saveMappingsMutateAsync } =
                extractionMutationHooks.useSaveMappingsMutation();
            const { mutateAsync: changeRowTypeMutateAsync } =
                extractionMutationHooks.useChangeRowTypeMutation();
            const { mutateAsync: flipRowSignMutateAsync } =
                extractionMutationHooks.useFlipRowSignMutation();
            const { mutateAsync: setDocumentTargetRowMutateAsync } =
                extractionMutationHooks.useSetDocumentTargetRowMutation();
            const { mutateAsync: editCellValuesMutateAsync } =
                extractionMutationHooks.useEditCellValuesMutation();
            const { mutateAsync: revertCellValuesMutateAsync } =
                extractionMutationHooks.useRevertCellValuesMutation();
            const { mutateAsync: changeTableMutateAsync } =
                extractionMutationHooks.useChangeTableMutation();
            const { mutateAsync: updateExtractionTypeMutateAsync } =
                extractionMutationHooks.useUpdateExtractionTypeMutation();
            const { mutateAsync: resetRowChangesMutateAsync } =
                extractionMutationHooks.useResetRowChangesMutation();
            const { mutateAsync: setColumnStateMutateAsync } =
                extractionMutationHooks.useSetColumnStateMutation();
            const { mutateAsync: batchSetColumnStateMutateAsync } =
                extractionMutationHooks.useBatchSetColumnStateMutation();
            const { mutateAsync: updateExtractionMutateAsync } =
                projectMutationHooks.useUpdateExtractionMutation();
            const { mutateAsync: discardExtractionMutateAsync } =
                extractionMutationHooks.useDiscardExtractionMutation();
            const setEditMode = useRootStoreSelector(
                state => state.setEditMode,
            );
            const resetStateTableMapping = useRootStoreSelector(
                state => state.resetStateTableMapping,
            );
            const resetExtractionsState = useRootStoreSelector(
                state => state.resetExtractionsState,
            );
            const setCloseExtractionModalOpenState = useRootStoreSelector(
                state => state.setCloseExtractionModalOpenState,
            );
            const resetStateOfAllDependencies = React.useCallback(() => {
                resetStateTableMapping();
                resetExtractionsState();
            }, [resetStateTableMapping, resetExtractionsState]);

            const onMount = React.useCallback(() => {}, []);

            const onUnmount = React.useCallback(() => {
                resetStateOfAllDependencies();
            }, [resetStateOfAllDependencies]);

            const setSelectedRows = useRootStoreSelector(
                state => state.setSelectedRows,
            );
            const setSelectedColumns = useRootStoreSelector(
                state => state.setSelectedColumns,
            );
            const onTableChange = React.useCallback(
                ({
                    extractionId,
                    table,
                }: {
                    projectId: number;
                    databaseId: number;
                    extractionId: number;
                    table: TTableLocation;
                }) => {
                    void changeTableMutateAsync({ extractionId, table });
                },
                [changeTableMutateAsync],
            );
            return {
                onMount,
                onUnmount,
                onMappingsAltered: ({
                    changes,
                    extractionId,
                    accountOptions,
                }) => {
                    for (const { rowId, accountCode } of changes) {
                        if (accountCode === null) {
                            omitRow({ rowId });
                        } else {
                            changeMapping({
                                rowId,
                                newValue: accountCode,
                                accountOptions,
                            });
                        }
                    }
                    void saveMappingsMutateAsync({
                        extractionId,
                        updatedRows: changes.map(({ rowId, accountCode }) => ({
                            rowId,
                            accountCode,
                        })),
                    });
                    Userpilot.track("odin_user_changed_mapping");
                },
                onRowTypeChanged: ({ rowId, rowType, extractionId }) => {
                    void changeRowTypeMutateAsync({
                        extractionId,
                        rowIds: [rowId],
                        rowType,
                    });
                },
                onRowSignFlipped: ({ rowId, extractionId, flipped }) => {
                    void flipRowSignMutateAsync({
                        extractionId,
                        rowIds: [rowId],
                        flipped,
                    });
                },
                onDocumentTargetRowSelected: ({ rowId, extractionId }) => {
                    void setDocumentTargetRowMutateAsync({
                        extractionId,
                        rowId,
                    }).catch(() => {});
                },
                onCellValuesEdited: ({ extractionId, cells }) => {
                    void editCellValuesMutateAsync({ extractionId, cells });
                },
                onCellValuesReverted: ({ extractionId, cells }) => {
                    void revertCellValuesMutateAsync({ extractionId, cells });
                },
                onCloseWithoutSaveRequested: extractionExit.exit,
                onCloseAndSaveRequested: extractionExit.exit,
                onNextClicked: async ({
                    projectId,
                    databaseId,
                    extractionId,
                }) => {
                    try {
                        await updateExtractionMutateAsync({
                            projectId,
                            databaseId,
                            extractionId,
                            extractionStatus: "draft",
                        });
                        navigate.toPageExtractionReview({
                            projectId: String(projectId),
                            databaseId: String(databaseId),
                            extractionId: String(extractionId),
                        });
                    } catch {
                        // Error notification handled by mutation onError
                    }
                },
                onExtractionIsNotDraft: ({
                    projectId,
                    extractionId,
                    databaseId,
                }) => {
                    navigate.toPageExtractionReview({
                        projectId: String(projectId),
                        databaseId: String(databaseId),
                        extractionId: String(extractionId),
                    });
                },
                onDiscardExtraction: ({
                    projectId,
                    databaseId,
                    extractionId,
                }) => {
                    void extractionExit.discardAndExit({
                        projectId,
                        databaseId,
                        discard: () =>
                            discardExtractionMutateAsync({
                                extractionId,
                                projectId,
                                databaseId,
                            }),
                    });
                },
                setCloseExtractionModalOpenState,
                onTableChange,
                onUpdateExtractionType: ({ extractionId, extractionType }) => {
                    void updateExtractionTypeMutateAsync({
                        extractionId,
                        extractionType,
                    })
                        .then(() => {
                            addNotificationWithDefaultTimeout({
                                kind: "toast",
                                severity: "success",
                                title: "Extraction type updated",
                            });
                        })
                        .catch(() => {
                            addNotificationWithDefaultTimeout({
                                kind: "toast",
                                severity: "danger",
                                title: "Failed to update extraction type",
                            });
                        });
                },
                setSelectedRows,
                setSelectedColumns,
                updateSelectedRowsType: (
                    extractionId: number,
                    rowType: TRowType,
                ) => {
                    void changeRowTypeMutateAsync({
                        extractionId,
                        rowIds: storeRoot.getState().selectedRows,
                        rowType,
                    })
                        .then(() => {
                            addNotificationWithDefaultTimeout({
                                message: "Row type updated successfully",
                                severity: "success",
                                title: "Row type updated",
                                kind: "toast",
                            });
                        })
                        .catch(() => {
                            addNotificationWithDefaultTimeout({
                                message: "Row type update failed",
                                severity: "danger",
                                title: "Row type update failed",
                                kind: "toast",
                            });
                        });
                    setEditMode("off");
                },
                flipSelectedRowsSign: (
                    extractionId: number,
                    flipped: boolean,
                ) => {
                    void flipRowSignMutateAsync({
                        extractionId,
                        rowIds: storeRoot.getState().selectedRows,
                        flipped,
                    })
                        .then(() => {
                            addNotificationWithDefaultTimeout({
                                message: "Row sign updated successfully",
                                severity: "success",
                                title: "Row sign updated",
                                kind: "toast",
                            });
                        })
                        .catch(() => {
                            addNotificationWithDefaultTimeout({
                                message: "Row sign update failed",
                                severity: "danger",
                                title: "Row sign update failed",
                                kind: "toast",
                            });
                        });
                    setEditMode("off");
                },
                resetRowChanges: async ({ extractionId, rowIds }) => {
                    try {
                        await resetRowChangesMutateAsync({
                            extractionId,
                            rowIds,
                        });
                        addNotificationWithDefaultTimeout({
                            message: "Row changes reset successfully",
                            severity: "success",
                            title: "Row changes reset",
                            kind: "toast",
                        });
                        setSelectedRows([]);
                        setEditMode("off");
                    } catch {
                        addNotificationWithDefaultTimeout({
                            message: "Row changes reset failed",
                            severity: "danger",
                            title: "Row changes reset failed",
                            kind: "toast",
                        });
                    }
                },
                setEditMode,
                setColumnState: async (extractionId, state, columnId) => {
                    try {
                        await setColumnStateMutateAsync({
                            extractionId,
                            state,
                            columnId,
                        });
                        addNotificationWithDefaultTimeout({
                            message: "Column state updated successfully",
                            severity: "success",
                            title: "Column state updated",
                            kind: "toast",
                        });
                    } catch {
                        addNotificationWithDefaultTimeout({
                            message: "Column state update failed",
                            severity: "danger",
                            title: "Column state update failed",
                            kind: "toast",
                        });
                    }
                },
                batchSetColumnState: async (extractionId, columnIds, state) => {
                    try {
                        const { failures } =
                            await batchSetColumnStateMutateAsync({
                                extractionId,
                                columnIds,
                                state,
                            });
                        setSelectedColumns([]);
                        if (failures === 0) {
                            addNotificationWithDefaultTimeout({
                                message: "Column state updated successfully",
                                severity: "success",
                                title: "Column state updated",
                                kind: "toast",
                            });
                        } else {
                            addNotificationWithDefaultTimeout({
                                message: "Some columns failed to update",
                                severity: "danger",
                                title: "Column state update failed",
                                kind: "toast",
                            });
                        }
                    } catch {
                        addNotificationWithDefaultTimeout({
                            message: "Column state update failed",
                            severity: "danger",
                            title: "Column state update failed",
                            kind: "toast",
                        });
                    }
                },
            };
        },
        useLlmReExtraction: () => {
            const {
                mutateAsync: llmReExtractMutateAsync,
                reset: llmReExtractReset,
                status: llmReExtractMutationStatus,
                error: llmReExtractRawError,
            } = extractionMutationHooks.useLlmReExtractMutation();
            const llmReExtractStatus:
                | "idle"
                | "pending"
                | "resolved"
                | "rejected" =
                llmReExtractMutationStatus === "pending"
                    ? "pending"
                    : llmReExtractMutationStatus === "success"
                      ? "resolved"
                      : llmReExtractMutationStatus === "error"
                        ? "rejected"
                        : "idle";
            const llmReExtractError =
                llmReExtractMutationStatus === "error"
                    ? llmReExtractRawError instanceof Error
                        ? llmReExtractRawError.message
                        : "LLM re-extraction failed"
                    : null;
            const { data: llmReExtractProgressData } = useQuery({
                queryKey: llmReExtractProgressQueryKey,
                queryFn: () => ({
                    progress: null as number | null,
                    message: null as string | null,
                }),
                enabled: false,
                initialData: { progress: null, message: null },
            });
            return {
                llmReExtractStatus,
                llmReExtractProgress:
                    llmReExtractProgressData?.progress ?? null,
                llmReExtractMessage: llmReExtractProgressData?.message ?? null,
                llmReExtractError,
                llmReExtract: async ({ extractionId }) => {
                    queryClient.setQueryData(llmReExtractProgressQueryKey, {
                        progress: 0,
                        message: null,
                    });
                    await llmReExtractMutateAsync({
                        extractionId,
                        onProgress: ({ percentage, message }) => {
                            queryClient.setQueryData(
                                llmReExtractProgressQueryKey,
                                {
                                    progress: percentage,
                                    message,
                                },
                            );
                        },
                    });
                },
                resetLlmReExtractState: () => {
                    llmReExtractReset();
                    queryClient.setQueryData(llmReExtractProgressQueryKey, {
                        progress: null,
                        message: null,
                    });
                },
            };
        },
        ComponentSidebar: ViewExtractionSidebar,
        ComponentConcurrentUsersIndicator: ViewConcurrentUsersIndicator,
        ComponentConcurrentUsersBanner: ViewConcurrentUsersBanner,
        ComponentConcurrentUsersStartSessionOnMountAndStopOnUnmount:
            HeadlessViewConcurrentUsersStartSessionOnMountAndStopOnUnmount,
    });

    const ViewExtractionReview = createViewExtractionReview({
        useState: ({ extractionId, projectId, databaseId }) => {
            const {
                data: standardizedData,
                isPending: standardizedIsPending,
                isError: standardizedIsError,
            } = extractionQueryHooks.useStandardizedExtractionQuery(
                {
                    extractionId,
                    projectId: projectId ?? null,
                    databaseId: databaseId ?? null,
                },
                {
                    enabled:
                        projectId !== undefined && databaseId !== undefined,
                },
            );
            const {
                data: overlappingPeriodsData,
                isPending: overlappingPeriodsIsPending,
            } = extractionQueryHooks.useOverlappingPeriodsQuery(
                {
                    extractionId,
                    projectId: projectId ?? null,
                    databaseId: databaseId ?? null,
                },
                {
                    enabled:
                        projectId !== undefined && databaseId !== undefined,
                },
            );
            const standardizedDataWithOverlaps = React.useMemo(() => {
                if (standardizedData === undefined) return undefined;
                return buildStandardizedExtractionQueryData({
                    extraction: standardizedData.raw,
                    overlappingPeriods: overlappingPeriodsData ?? [],
                });
            }, [overlappingPeriodsData, standardizedData]);
            const updateStandardizedExtractionRowsLoaded = useRootStoreSelector(
                state => state.updateStandardizedExtractionRowsLoaded,
            );
            const setRowsAlreadyDismissed = useRootStoreSelector(
                state => state.setRowsAlreadyDismissed,
            );
            const convertToDraftModalOpen = useRootStoreSelector(
                state => state.convertToDraftModalOpen,
            );
            const { data: conflictingExtractionsData } =
                extractionQueryHooks.useConflictExtractionsQuery(
                    {
                        extractionId,
                        projectId: projectId ?? null,
                        databaseId: databaseId ?? null,
                    },
                    {
                        enabled:
                            convertToDraftModalOpen &&
                            projectId !== undefined &&
                            databaseId !== undefined,
                    },
                );
            const { data: projectFilesData } =
                projectQueryHooks.useProjectFilesQuery(
                    projectId ?? 0,
                    databaseId ?? 0,
                    {
                        enabled:
                            projectId !== undefined && databaseId !== undefined,
                    },
                );
            const dismissalsSeededFromRef =
                React.useRef<typeof standardizedData>(undefined);
            React.useEffect(() => {
                if (!standardizedDataWithOverlaps) return;
                updateStandardizedExtractionRowsLoaded({
                    updatedRows: standardizedDataWithOverlaps.dto,
                });
                // Re-seed dismissals only when standardized data reloads, not
                // on every overlapping-periods refetch — else local dismissals
                // get clobbered.
                if (dismissalsSeededFromRef.current !== standardizedData) {
                    dismissalsSeededFromRef.current = standardizedData;
                    setRowsAlreadyDismissed({
                        rows: standardizedDataWithOverlaps.dto.rowsDismissed,
                    });
                }
            }, [
                setRowsAlreadyDismissed,
                standardizedData,
                standardizedDataWithOverlaps,
                updateStandardizedExtractionRowsLoaded,
            ]);
            const loadStandardizedExtractionStatus = standardizedIsPending
                ? "pending"
                : standardizedIsError
                  ? "rejected"
                  : standardizedData !== undefined
                    ? "resolved"
                    : "idle";
            const extractionStatus = standardizedData?.raw.status ?? null;
            const isCompletionDataPending =
                extractionStatus === "draft" && overlappingPeriodsIsPending;
            const reviewData = useRootStoreSelector(
                state => state.extractionStandardizedRowsLoaded?.rows || null,
            );
            const reviewDataNonOperating = useRootStoreSelector(
                state =>
                    state.extractionStandardizedRowsLoaded?.nonOperatingRows ||
                    null,
            );
            const dealName = standardizedData?.raw.dealName ?? null;
            const standardizedTotalNetIncomeOriginal = useRootStoreSelector(
                state =>
                    state.extractionStandardizedRowsLoaded
                        ?.standardizedTotalNetIncomeOriginal ?? null,
            );
            const standardizedOriginalNetIncomeTotalName = useRootStoreSelector(
                state =>
                    state.extractionStandardizedRowsLoaded
                        ?.standardizedOriginalNetIncomeTotalName ?? null,
            );
            const standardizedTotalNetIncomeAltered = useRootStoreSelector(
                state =>
                    state.extractionStandardizedRowsLoaded
                        ?.standardizedTotalNetIncomeAltered ?? null,
            );
            const columnDefsOperatingStatements = useRootStoreSelector(
                state =>
                    state.extractionStandardizedRowsLoaded
                        ?.columnDefsOperatingStatements || null,
            );
            const errorGroups = useRootStoreSelector(state =>
                state.getOutliers()({
                    rowsDismissed: state.rowsDismissed,
                }),
            );
            const saveExtractionCompleteModalOpen = useRootStoreSelector(
                state => state.saveExtractionCompleteModalOpen,
            );
            const closeExtractionModalOpen = useRootStoreSelector(
                state => state.closeExtractionModalOpen,
            );
            const activeColumnsFromStore = useRootStoreSelector(
                state => state.extractionStandardizedActiveColumns,
            );
            // Prefer the store: it tracks the user's checkbox selections.
            // The standardized DTO is only the initial server snapshot and
            // is overwritten into the store by the effect above; falling
            // back to the DTO covers the brief window before that effect
            // runs after a fresh data load.
            const activeColumns =
                activeColumnsFromStore ??
                standardizedDataWithOverlaps?.dto.activeColumns ??
                null;
            const extractionsInfo = React.useMemo(
                () =>
                    projectFilesData?.transformedFileView
                        .filter(
                            (file): file is FileExtractionInfoWithParent =>
                                "parentFileName" in file &&
                                "extractionId" in file &&
                                file.parentFileName !== null,
                        )
                        .map(file => ({
                            fileName: file.parentFileName,
                            extractionId: file.extractionId,
                            sheetName: file.sheetName,
                            uploadedAt: file.uploadedAt,
                            uploadedBy: file.uploadedBy,
                            completedAt: file.completedAt,
                        })) ?? null,
                [projectFilesData],
            );
            const conflictingExtractions = conflictingExtractionsData ?? null;
            const extractionType = standardizedData?.raw.extractionType ?? null;

            return {
                conflictingExtractions,
                loadStandardizedExtractionStatus,
                columnDefsOperatingStatements,
                extractionStatus,
                reviewData,
                reviewDataBelowTheLine: reviewDataNonOperating,
                dealName,
                standardizedTotalNetIncomeOriginal,
                standardizedOriginalNetIncomeTotalName,
                standardizedTotalNetIncomeAltered,
                errorGroups,
                convertToDraftModalOpen,
                saveExtractionCompleteModalOpen,
                closeExtractionModalOpen,
                activeColumns,
                extractionsInfo,
                extractionType,
                isCompletionDataPending,
            };
        },
        useCases: () => {
            const {
                mutateAsync: updateExtractionMutateAsync,
                isPending: isUpdateExtractionPending,
            } = projectMutationHooks.useUpdateExtractionMutation();
            const { mutateAsync: convertToDraftMutateAsync } =
                extractionMutationHooks.useConvertExtractionsToDraftMutation();
            const { mutateAsync: updateExtractionTypeMutateAsync } =
                extractionMutationHooks.useUpdateExtractionTypeMutation();
            const { mutateAsync: discardExtractionMutateAsync } =
                extractionMutationHooks.useDiscardExtractionMutation();
            const showExportModal = useRootStoreSelector(
                state => state.showExportModal,
            );
            const setConvertToDraftModalOpenState = useRootStoreSelector(
                state => state.setConvertToDraftModalOpenState,
            );
            const setSaveExtractionCompleteModalOpenState =
                useRootStoreSelector(
                    state => state.setSaveExtractionCompleteModalOpenState,
                );
            const setCloseExtractionModalOpenState = useRootStoreSelector(
                state => state.setCloseExtractionModalOpenState,
            );
            const setActiveColumn = useRootStoreSelector(
                state => state.setActiveColumn,
            );
            const resetStateTableMapping = useRootStoreSelector(
                state => state.resetStateTableMapping,
            );
            const resetExtractionsState = useRootStoreSelector(
                state => state.resetExtractionsState,
            );
            const resetStateOfAllDependencies = React.useCallback(() => {
                resetStateTableMapping();
                resetExtractionsState();
            }, [resetStateTableMapping, resetExtractionsState]);

            const onMount = React.useCallback(() => {}, []);

            const onUnmount = React.useCallback(() => {
                resetStateOfAllDependencies();
            }, [resetStateOfAllDependencies]);

            return {
                showLoadingOverlay: isUpdateExtractionPending,
                onMount,
                onUnmount,
                onCloseRequested: ({ projectId, databaseId }) => {
                    extractionExit.exit({ projectId, databaseId });
                },
                onBackClicked: ({ projectId, databaseId, extractionId }) =>
                    navigate.toPageExtractionMappings({
                        projectId: String(projectId),
                        databaseId: String(databaseId),
                        extractionId: String(extractionId),
                    }),
                // Using mutateAsync + try/catch to ensure the API call
                // completes before navigation. Some navigations trigger
                // full page reloads that would cancel in-flight requests.
                onSaveExtractionClicked: async ({
                    projectId,
                    databaseId,
                    extractionId,
                }) => {
                    try {
                        const state = storeRoot.getState();
                        await updateExtractionMutateAsync({
                            projectId,
                            databaseId,
                            extractionId,
                            extractionStatus: "completed",
                            activePeriods: state.getActivePeriods(state),
                        });
                        storeRoot.getState().addNotificationWithDefaultTimeout({
                            kind: "toast",
                            severity: "info",
                            title: "Extraction completed",
                        });
                        Userpilot.track("odin_user_saves_extraction");
                        extractionExit.exit({
                            projectId,
                            databaseId,
                        });
                    } catch {
                        // Error notification handled by mutation onError
                    }
                },
                onSaveDraftClicked: async ({
                    projectId,
                    databaseId,
                    extractionId,
                }) => {
                    try {
                        await updateExtractionMutateAsync({
                            projectId,
                            databaseId,
                            extractionId,
                            extractionStatus: "draft",
                        });
                        storeRoot.getState().addNotificationWithDefaultTimeout({
                            kind: "toast",
                            severity: "info",
                            title: "Draft saved",
                        });
                        extractionExit.exit({
                            projectId,
                            databaseId,
                        });
                    } catch {
                        // Error notification handled by mutation onError
                    }
                },
                onConvertToDraftClicked: ({
                    projectId,
                    databaseId,
                    extractionId,
                    extractionIds,
                }) => {
                    void convertToDraftMutateAsync({
                        projectId,
                        databaseId,
                        extractionId,
                        extractionIds,
                    }).then(() => {
                        window.location.reload();
                    });
                },
                onExportExtractionClicked: ({
                    extractionId,
                    extractionType,
                    projectId,
                    databaseId,
                }: {
                    extractionId: number;
                    extractionType: TExtractionType;
                    projectId: number;
                    databaseId: number;
                }) => {
                    showExportModal({
                        extractions: [{ extractionId, extractionType }],
                        projectId,
                        databaseId,
                    });
                },
                onDiscardExtraction: ({
                    projectId,
                    databaseId,
                    extractionId,
                }) => {
                    void extractionExit.discardAndExit({
                        projectId,
                        databaseId,
                        discard: () =>
                            discardExtractionMutateAsync({
                                extractionId,
                                projectId,
                                databaseId,
                            }),
                    });
                },
                setConvertToDraftModalOpenState,
                setSaveExtractionCompleteModalOpenState,
                setCloseExtractionModalOpenState,
                setActiveColumn,
                checkConflictingExtractionsForConversionToDraftAndOpenModal: ({
                    projectId,
                    databaseId,
                    extractionId,
                }) => {
                    void queryClient
                        .ensureQueryData(
                            extractionQueryHooks.getConflictExtractionsQueryOptions(
                                {
                                    projectId,
                                    databaseId,
                                    extractionId,
                                },
                            ),
                        )
                        .then(() => {
                            setConvertToDraftModalOpenState(true);
                        })
                        .catch(() => {
                            addNotificationWithDefaultTimeout({
                                kind: "toast",
                                severity: "danger",
                                title: "Failed to check for conflicting extractions",
                            });
                        });
                },
                onUpdateExtractionType: ({ extractionId, extractionType }) => {
                    void updateExtractionTypeMutateAsync({
                        extractionId,
                        extractionType,
                    })
                        .then(() => {
                            addNotificationWithDefaultTimeout({
                                kind: "toast",
                                severity: "success",
                                title: "Extraction type updated",
                            });
                        })
                        .catch(() => {
                            addNotificationWithDefaultTimeout({
                                kind: "toast",
                                severity: "danger",
                                title: "Failed to update extraction type",
                            });
                        });
                },
            };
        },
        ComponentSidebar: ViewExtractionStandardizedSidebar,
        ComponentConcurrentUsersIndicator: ViewConcurrentUsersIndicator,
        ComponentConcurrentUsersBanner: ViewConcurrentUsersBanner,
        ComponentConcurrentUsersStartSessionOnMountAndStopOnUnmount:
            HeadlessViewConcurrentUsersStartSessionOnMountAndStopOnUnmount,
    });

    const ViewExportModal = createViewExportModal({
        useState: () => {
            const showModal = useRootStoreSelector(
                state => state.exportModalIsVisible,
            );
            const exportConfig = useRootStoreSelector(
                state => state.exportConfig,
            );
            const exportData = useRootStoreSelector(state => state.exportData);
            const exportProjectId = useRootStoreSelector(
                state => state.exportProjectId,
            );
            const exportDatabaseId = useRootStoreSelector(
                state => state.exportDatabaseId,
            );
            const { data: projectDetail } =
                projectQueryHooks.useProjectDetailQuery(
                    exportProjectId ?? 0,
                    exportDatabaseId ?? 0,
                    { enabled: exportProjectId !== null },
                );
            const nonOperatingAccountsInCoa =
                projectDetail?.nonOperatingAccountsInCoa ?? false;
            return {
                showModal,
                exportConfig,
                exportData,
                nonOperatingAccountsInCoa,
            };
        },
        useCases: () => {
            const updateExportConfig = useRootStoreSelector(
                state => state.updateExportConfig,
            );
            const onCancel = useRootStoreSelector(state => state.cancelExport);
            const typedExportConfig = useRootStoreSelector(
                state => state.typedExportConfig,
            );
            const exportData = useRootStoreSelector(state => state.exportData);
            const cancelExport = useRootStoreSelector(
                state => state.cancelExport,
            );
            const onConfirm = async ({
                nonOperatingAccountsInCoa,
            }: {
                nonOperatingAccountsInCoa: boolean;
            }) => {
                if (exportData && exportData.tag === "extractions") {
                    const extractionIds = exportData.extractions
                        .filter(
                            extraction =>
                                extraction.extractionType !==
                                "rent_roll_extraction",
                        )
                        .map(extraction => extraction.extractionId);

                    if (
                        extractionIds.length === 1 &&
                        extractionIds[0] !== undefined
                    ) {
                        try {
                            const response =
                                await apiODIN.downloadExtractionExport({
                                    extractionId: extractionIds[0],
                                    exportConfig: typedExportConfig(),
                                    nonOperatingAccountsInCoa,
                                });

                            const contentDispositionHeader =
                                response.response.headers.get(
                                    "Content-Disposition",
                                );
                            let filename = "downloaded_file";
                            if (contentDispositionHeader !== null) {
                                const filenameFromDisposition =
                                    contentDisposition.parse(
                                        contentDispositionHeader,
                                    ).parameters["filename"];
                                if (filenameFromDisposition !== undefined) {
                                    filename = filenameFromDisposition;
                                }
                            }

                            const blob = response.data as Blob;
                            downloadFile(blob, filename);
                            addNotificationWithDefaultTimeout({
                                title: "Extraction exported successfully",
                                kind: "toast",
                                severity: "success",
                            });
                        } catch {
                            addNotificationWithDefaultTimeout({
                                title: "Failed to export extraction",
                                kind: "toast",
                                severity: "danger",
                            });
                        }
                    } else {
                        try {
                            const chunks = chunkIdArray(extractionIds);
                            for (const chunk of chunks) {
                                const response =
                                    await apiODIN.downloadExtractionExportZip({
                                        extractionIds: chunk,
                                        exportConfig: typedExportConfig(),
                                        nonOperatingAccountsInCoa,
                                    });
                                const contentDispositionHeader =
                                    response.response.headers.get(
                                        "Content-Disposition",
                                    );
                                let filename = "downloaded_file";
                                if (contentDispositionHeader !== null) {
                                    const filenameFromDisposition =
                                        contentDisposition.parse(
                                            contentDispositionHeader,
                                        ).parameters["filename"];
                                    if (filenameFromDisposition !== undefined) {
                                        filename = filenameFromDisposition;
                                    }
                                }
                                const blob = (await response.data) as Blob;
                                downloadFile(blob, filename);
                            }
                            addNotificationWithDefaultTimeout({
                                title: "Extractions exported successfully",
                                kind: "toast",
                                severity: "success",
                            });
                        } catch {
                            addNotificationWithDefaultTimeout({
                                title: "Extractions export failed",
                                kind: "toast",
                                severity: "danger",
                            });
                        }
                    }
                } else if (exportData && exportData.tag === "annualization") {
                    if (exportData.annualizationType === null) {
                        throw new Error(
                            "Annualization type is null in annualization export",
                        );
                    }
                    const intervalNumber = getInterval(exportData.interval);
                    try {
                        const response =
                            await apiODIN.downloadAnnualizationFile({
                                projectId: exportData.projectId,
                                databaseId: exportData.databaseId,
                                startDate: exportData.startDate,
                                endDate: exportData.endDate,
                                interval: intervalNumber,
                                exportConfig: typedExportConfig(
                                    exportData.reconciliation,
                                ),
                                nonOperatingAccountsInCoa,
                                annualizationType: exportData.annualizationType,
                                reconciliation: exportData.reconciliation,
                            });
                        const contentDispositionHeader =
                            response.response.headers.get(
                                "Content-Disposition",
                            );
                        let filename = "downloaded_file";
                        if (contentDispositionHeader !== null) {
                            const filenameFromDisposition =
                                contentDisposition.parse(
                                    contentDispositionHeader,
                                ).parameters["filename"];
                            if (filenameFromDisposition !== undefined) {
                                filename = decodeURIComponent(
                                    filenameFromDisposition,
                                );
                            }
                        }

                        const blob = (await response.data) as Blob;
                        downloadFile(blob, filename);
                        addNotificationWithDefaultTimeout({
                            title: "Operating Statement exported successfully",
                            kind: "toast",
                            severity: "success",
                        });
                    } catch (error) {
                        addNotificationWithDefaultTimeout({
                            title: "Failed to export Operating Statement",
                            message: apiErrorDetail(error) ?? undefined,
                            kind: "toast",
                            severity: "danger",
                        });
                    }
                }
                cancelExport();
                Userpilot.track("odin_user_generates_export");
            };
            return {
                updateExportConfig,
                onConfirm,
                onCancel,
            };
        },
    });

    const ViewUserPolicies = createViewUserPolicies({
        useState: () => {
            const { data: userData } = userQueryHooks.useUserDataQuery();
            const userConsent = userData?.userConsent ?? {
                userConsentLastAccepted: null,
                userConsentValid: false,
            };
            const userRegion = userData?.userRegion ?? null;
            return { userConsent, userRegion };
        },
    });

    const ViewCreateChartOfAccounts = createViewCreateChartOfAccounts({
        useState: () => {
            const {
                data: coas,
                isPending: coasIsPending,
                isError: coasIsError,
            } = coaQueryHooks.useAllCoasForUserQuery();
            const excludedCoANames = React.useMemo(() => {
                return isNullable(coas)
                    ? []
                    : coas.map(coa => coa.name.trim().toLowerCase());
            }, [coas]);
            const coasRequestStatus = coasIsPending
                ? "pending"
                : coasIsError
                  ? "rejected"
                  : "resolved";
            const {
                data: adminDatabasesData,
                isPending: adminDatabasesIsPending,
                isError: adminDatabasesIsError,
            } = adminQueryHooks.useAdminDatabasesQuery();
            const adminDatabases = adminDatabasesData ?? {};
            const getAdminDatabasesState = adminDatabasesIsPending
                ? "pending"
                : adminDatabasesIsError
                  ? "rejected"
                  : "resolved";
            const createCoASelectedDatabases = useRootStoreSelector(
                state => state.createCoASelectedDatabases,
            );
            const {
                data: availableCoAsData,
                isPending: availableCoAsIsPending,
                isError: availableCoAsIsError,
            } = adminQueryHooks.useAdminCoasForDatabasesQuery(
                createCoASelectedDatabases,
            );
            const availableCoAsForDatabaseId = availableCoAsData ?? [];
            const availableCoAsForDatabaseIdState =
                createCoASelectedDatabases.length === 0
                    ? "idle"
                    : availableCoAsIsPending
                      ? "pending"
                      : availableCoAsIsError
                        ? "rejected"
                        : "resolved";
            const {
                data: userData,
                isPending: userDataIsPending,
                isError: userDataIsError,
            } = userQueryHooks.useUserDataQuery();
            const userDataState = userDataIsPending
                ? "pending"
                : userDataIsError
                  ? "rejected"
                  : "resolved";
            const isGlobalAdmin = userData?.userInfo?.isGlobalAdmin ?? false;
            const isProdaUser = userData?.userInfo?.isProdaUser ?? false;
            const {
                data: companiesData,
                isPending: companiesIsPending,
                isError: companiesIsError,
            } = databaseQueryHooks.useCompaniesQuery();
            const companies = companiesData ?? null;
            const getCompaniesState = companiesIsPending
                ? "pending"
                : companiesIsError
                  ? "rejected"
                  : "resolved";
            const isCreateDraftCoaPending =
                useMutationState({
                    filters: {
                        mutationKey: ["coa", "createDraft"],
                        status: "pending",
                    },
                }).length > 0;
            const createDraftCoaMutationStates = useMutationState({
                filters: {
                    mutationKey: ["coa", "createDraft"],
                },
                select: mutation => ({
                    status: mutation.state.status,
                    submittedAt: mutation.state.submittedAt,
                }),
            });
            const isCreateDraftCoaError =
                getLatestMutationStatus(createDraftCoaMutationStates) ===
                "error";
            return {
                excludedCoANames,
                coasRequestStatus,
                adminDatabases,
                availableCoAsForDatabaseIdState,
                availableCoAsForDatabaseId,
                userDataState,
                isGlobalAdmin,
                getAdminDatabasesState,
                createCoASelectedDatabases,
                companies,
                getCompaniesState,
                isProdaUser,
                isCreateDraftCoaPending,
                isCreateDraftCoaError,
            };
        },
        useCases: () => {
            const invalidateCoasForUser = React.useCallback(async () => {
                await queryClient.invalidateQueries({
                    queryKey: coaKeys.allForUser(),
                });
            }, []);

            const onMount = React.useCallback(() => {
                void queryClient.invalidateQueries({
                    queryKey: databaseKeys.companies(),
                });
            }, []);

            const onUnmount = React.useCallback(() => {
                void queryClient.removeQueries({
                    queryKey: coaKeys.allForUser(),
                });
            }, []);
            const createDraftCoaMutation =
                coaMutationHooks.useCreateDraftCoaMutation();
            const handleSubmitCreateCoA = async (data: TNewCoADefinition) => {
                const newCoaId = await createDraftCoaMutation.mutateAsync(data);
                navigate.toChartOfAccountsEditDetails({
                    coaId: String(newCoaId),
                });
            };
            const setCreateCoASelectedDatabases = useRootStoreSelector(
                state => state.setCreateCoASelectedDatabases,
            );
            return {
                onMount,
                onUnmount,
                onSubmit: handleSubmitCreateCoA,
                getExcludedCoANames: invalidateCoasForUser,
                setCreateCoASelectedDatabases,
            };
        },
    });

    const ViewChartsOfAccountsDashboard = createViewChartsOfAccountsDashboard({
        useState: () => {
            const {
                data: coas,
                isPending: coasIsPending,
                isError: coasIsError,
            } = coaQueryHooks.useAllCoasForUserQuery();
            const coasRequestStatus = coasIsPending
                ? "pending"
                : coasIsError
                  ? "rejected"
                  : "resolved";
            const {
                data: adminDatabasesData,
                isPending: adminDatabasesIsPending,
                isError: adminDatabasesIsError,
            } = adminQueryHooks.useAdminDatabasesQuery();
            const adminDatabases = adminDatabasesData ?? {};
            const getAdminDatabasesState = adminDatabasesIsPending
                ? "pending"
                : adminDatabasesIsError
                  ? "rejected"
                  : "resolved";
            const dashboardSelectedDatabaseIds = useRootStoreSelector(
                state => state.dashboardSelectedDatabaseIds,
            );
            const {
                data: availableCoAsData,
                isPending: availableCoAsIsPending,
                isError: availableCoAsIsError,
            } = adminQueryHooks.useAdminCoasForDatabasesQuery(
                dashboardSelectedDatabaseIds,
            );
            const availableCoAsForDatabaseId = availableCoAsData ?? [];
            const availableCoAsForDatabaseIdState =
                dashboardSelectedDatabaseIds.length === 0
                    ? "idle"
                    : availableCoAsIsPending
                      ? "pending"
                      : availableCoAsIsError
                        ? "rejected"
                        : "resolved";
            const {
                data: userData,
                isPending: userDataIsPending,
                isError: userDataIsError,
            } = userQueryHooks.useUserDataQuery();
            const userDataState = userDataIsPending
                ? "pending"
                : userDataIsError
                  ? "rejected"
                  : "resolved";
            const dashboardSelectedCoAIds = useRootStoreSelector(
                state => state.dashboardSelectedCoAIds,
            );
            const isGlobalAdmin = userData?.userInfo?.isGlobalAdmin ?? false;
            const isProdaUser = userData?.userInfo?.isProdaUser ?? false;
            const createCoASelectedDatabases = useRootStoreSelector(
                state => state.createCoASelectedDatabases,
            );
            const {
                data: companiesData,
                isPending: companiesIsPending,
                isError: companiesIsError,
            } = databaseQueryHooks.useCompaniesQuery();
            const companies = companiesData ?? null;
            const getCompaniesState = companiesIsPending
                ? "pending"
                : companiesIsError
                  ? "rejected"
                  : "resolved";
            const isCreateDraftCoaPending =
                useMutationState({
                    filters: {
                        mutationKey: ["coa", "createDraft"],
                        status: "pending",
                    },
                }).length > 0;
            const createDraftCoaMutationStates = useMutationState({
                filters: {
                    mutationKey: ["coa", "createDraft"],
                },
                select: mutation => ({
                    status: mutation.state.status,
                    submittedAt: mutation.state.submittedAt,
                }),
            });
            const isCreateDraftCoaError =
                getLatestMutationStatus(createDraftCoaMutationStates) ===
                "error";
            return {
                coas: coas ?? null,
                adminDatabases,
                coasRequestStatus,
                dashboardSelectedDatabaseIds,
                availableCoAsForDatabaseIdState,
                availableCoAsForDatabaseId,
                userDataState,
                dashboardSelectedCoAIds,
                isGlobalAdmin,
                getAdminDatabasesState,
                createCoASelectedDatabases,
                companies,
                getCompaniesState,
                isProdaUser,
                isCreateDraftCoaPending,
                isCreateDraftCoaError,
            };
        },
        useCases: () => {
            const invalidateAdmin = React.useCallback(() => {
                void queryClient.invalidateQueries({ queryKey: adminKeys.all });
            }, []);

            const onMount = React.useCallback(() => {
                void queryClient.invalidateQueries({
                    queryKey: databaseKeys.companies(),
                });
            }, []);

            const onUnmount = React.useCallback(() => {
                void queryClient.removeQueries({
                    queryKey: coaKeys.allForUser(),
                });
            }, []);

            const navigateToCoaDetails = (coaId: number) => {
                navigate.toChartsOfAccountsDetails({
                    coaId: coaId.toString(),
                });
            };
            const navigateToCoaEditDetails = (coaId: number) => {
                navigate.toChartOfAccountsEditDetails({
                    coaId: coaId.toString(),
                });
            };
            const navigateToCreateCoA = () => {
                navigate.toChartOfAccountsCreate();
            };

            const addAccountPermissionsMutation =
                coaMutationHooks.useAddAccountPermissionsMutation();
            const deleteAccountPermissionsMutation =
                coaMutationHooks.useDeleteAccountPermissionsMutation();
            const handleSubmitAddAccountPermissions = async () => {
                const state = storeRoot.getState();
                const databaseIds = state.dashboardSelectedDatabaseIds;
                const coaIds = state.dashboardSelectedCoAIds;
                if (databaseIds.length === 0 || coaIds.length === 0) {
                    addNotificationWithDefaultTimeout({
                        title: "Please select a database and a chart of accounts",
                        kind: "toast",
                        severity: "danger",
                    });
                    return;
                }
                await addAccountPermissionsMutation.mutateAsync({
                    databaseIds,
                    coaIds,
                });
            };
            const handleSubmitDeleteAccountPermissions = async () => {
                const state = storeRoot.getState();
                const databaseIds = state.dashboardSelectedDatabaseIds;
                const coaIds = state.dashboardSelectedCoAIds;
                if (databaseIds.length === 0 || coaIds.length === 0) {
                    addNotificationWithDefaultTimeout({
                        title: "Please select a database and a chart of accounts",
                        kind: "toast",
                        severity: "danger",
                    });
                    return;
                }
                await deleteAccountPermissionsMutation.mutateAsync({
                    databaseIds,
                    coaIds,
                });
            };
            const setDashboardSelectedDatabaseIds = useRootStoreSelector(
                state => state.setDashboardSelectedDatabaseIds,
            );
            const setDashboardSelectedCoAIds = useRootStoreSelector(
                state => state.setDashboardSelectedCoAIds,
            );
            const onAdminMount = React.useCallback(() => {
                invalidateAdmin();
            }, [invalidateAdmin]);
            const onAdminUnmount = React.useCallback(() => {
                void queryClient.removeQueries({
                    queryKey: adminKeys.coasForDatabases([]),
                    exact: false,
                });
            }, []);
            const createDraftCoaMutationDashboard =
                coaMutationHooks.useCreateDraftCoaMutation();
            const handleSubmitCreateCoA = async (data: TNewCoADefinition) => {
                const newCoaId =
                    await createDraftCoaMutationDashboard.mutateAsync(data);
                navigate.toChartsOfAccountsDetails({
                    coaId: String(newCoaId),
                });
            };
            const setCreateCoASelectedDatabases = useRootStoreSelector(
                state => state.setCreateCoASelectedDatabases,
            );
            return {
                onMount,
                onUnmount,
                navigateToCoaDetails,
                navigateToCoaEditDetails,
                navigateToCreateCoA,
                getAllAvailableCoAsForUser: async () => {
                    await queryClient.invalidateQueries({
                        queryKey: coaKeys.allForUser(),
                    });
                },
                handleSubmitCreateCoA,
                handleSubmitAddAccountPermissions,
                handleSubmitDeleteAccountPermissions,
                setDashboardSelectedDatabaseIds,
                setDashboardSelectedCoAIds,
                onAdminMount,
                onAdminUnmount,
                setCreateCoASelectedDatabases,
                createDraftCoa: (data: TNewCoADefinition) =>
                    createDraftCoaMutationDashboard.mutateAsync(data),
            };
        },
    });

    const ViewEditChartOfAccountsDefinition =
        createViewEditChartOfAccountsDefinition({
            useState: ({ coaId }) => {
                const { data: coas } = coaQueryHooks.useAllCoasForUserQuery();
                const excludedCoANames = React.useMemo(() => {
                    return isNullable(coas)
                        ? []
                        : coas.map(coa => coa.name.trim().toLowerCase());
                }, [coas]);
                const {
                    data: companiesData,
                    isPending: companiesIsPending,
                    isError: companiesIsError,
                } = databaseQueryHooks.useCompaniesQuery();
                const companies = companiesData ?? null;
                const getCompaniesState = companiesIsPending
                    ? "pending"
                    : companiesIsError
                      ? "rejected"
                      : "resolved";
                const {
                    data: coaDefinition,
                    isPending: coaDefIsPending,
                    isError: coaDefIsError,
                } = coaQueryHooks.useCoaDefinitionQuery(coaId);
                const getCoaState = coaDefIsPending
                    ? "pending"
                    : coaDefIsError
                      ? "rejected"
                      : "resolved";
                const isCoaUpdatePending =
                    useMutationState({
                        filters: {
                            mutationKey: ["coa", "updateDraft"],
                            status: "pending",
                            predicate: mutation =>
                                mutationVariablesHaveCoaId(
                                    mutation.state.variables,
                                    coaId,
                                ),
                        },
                    }).length > 0;
                const coaUpdateMutationStates = useMutationState({
                    filters: {
                        mutationKey: ["coa", "updateDraft"],
                        predicate: mutation =>
                            mutationVariablesHaveCoaId(
                                mutation.state.variables,
                                coaId,
                            ),
                    },
                    select: mutation => ({
                        status: mutation.state.status,
                        submittedAt: mutation.state.submittedAt,
                    }),
                });
                const isCoaUpdateError =
                    getLatestMutationStatus(coaUpdateMutationStates) ===
                    "error";
                const { data: accountsData } =
                    coaQueryHooks.useAccountsByCoaQuery(coaId);
                const coaContainsNonOperatingAccounts =
                    accountsData?.some(
                        account => account.type === "non-operating",
                    ) ?? false;
                const coaContainsSubcategories =
                    accountsData?.some(
                        account => account.subcategory !== null,
                    ) ?? false;

                return {
                    excludedCoANames,
                    companies,
                    getCompaniesState,
                    coaDefinition: coaDefinition ?? null,
                    getCoaState,
                    isCoaUpdatePending,
                    isCoaUpdateError,
                    coaContainsNonOperatingAccounts:
                        !!coaContainsNonOperatingAccounts,
                    coaContainsSubcategories: !!coaContainsSubcategories,
                };
            },
            useCases: () => {
                const updateDraftCoaMutation =
                    coaMutationHooks.useUpdateDraftCoaMutation();
                const navigateToChartOfAccountsEditDetails = (
                    coaId: number,
                ) => {
                    navigate.toChartOfAccountsEditDetails({
                        coaId: coaId.toString(),
                    });
                };
                const discardCoaMutation =
                    coaMutationHooks.useDiscardCoaMutation();
                const onDiscardCoA = async (coaId?: number) => {
                    if (coaId !== undefined) {
                        await discardCoaMutation.mutateAsync(coaId);
                        navigate.toChartsOfAccountsList();
                    }
                };
                const onSubmit = React.useCallback(
                    async (data: TNewCoADefinition, coaId?: number) => {
                        if (coaId !== undefined) {
                            await updateDraftCoaMutation.mutateAsync({
                                coaId,
                                data,
                            });
                        }
                    },
                    [updateDraftCoaMutation],
                );
                return {
                    getExcludedCoANames: async () => {
                        await queryClient.invalidateQueries({
                            queryKey: coaKeys.allForUser(),
                        });
                    },
                    onDiscardCoA,
                    onSubmit,
                    navigateToChartOfAccountsEditDetails,
                };
            },
        });

    const ViewEditChartOfAccountsDetails = createViewEditChartOfAccountsDetails(
        {
            useState: ({ coaId }) => {
                const { data: userData } = userQueryHooks.useUserDataQuery();
                const isGlobalAdmin =
                    userData?.userInfo?.isGlobalAdmin ?? false;
                const isProdaUser = userData?.userInfo?.isProdaUser ?? false;
                const {
                    data: coaDefinition,
                    isPending: coaDefIsPending,
                    isError: coaDefIsError,
                } = coaQueryHooks.useCoaDefinitionQuery(coaId);
                const coaDefinitionRequestStatus = coaDefIsPending
                    ? "pending"
                    : coaDefIsError
                      ? "rejected"
                      : "resolved";
                const {
                    data: accountsData,
                    isPending: accountsIsPending,
                    isError: accountsIsError,
                } = coaQueryHooks.useAccountsByCoaQuery(coaId);
                const allAccounts = useRootStoreSelector(
                    state => state.allAccounts,
                );
                const accountsEdited = useRootStoreSelector(
                    state => state.accountsEdited,
                );
                const accountsToBeDeleted = useRootStoreSelector(
                    state => state.accountsToBeDeleted,
                );
                const setAllAccounts = useRootStoreSelector(
                    state => state.setAllAccounts,
                );
                React.useEffect(() => {
                    if (accountsData) {
                        setAllAccounts(accountsData);
                    }
                }, [accountsData, setAllAccounts]);
                const allAccountsRequestStatus = accountsIsPending
                    ? "pending"
                    : accountsIsError
                      ? "rejected"
                      : "resolved";
                return {
                    coaDefinition: coaDefinition ?? null,
                    coaDefinitionRequestStatus,
                    isGlobalAdmin,
                    isProdaUser,
                    allAccounts,
                    allAccountsRequestStatus,
                    accountsEdited,
                    accountsToBeDeleted,
                };
            },
            useCases: () => {
                const addAccount = useRootStoreSelector(
                    state => state.addAccount,
                );
                const navigateToChartsOfAccountsEditDefinition =
                    navigate.toChartOfAccountsEditDefinition;
                const navigateToChartsOfAccountsEditMappings =
                    navigate.toChartOfAccountsEditMappings;
                const updateAccount = useRootStoreSelector(
                    state => state.updateAccount,
                );
                const saveAccountsMutation =
                    coaMutationHooks.useSaveAccountsMutation();
                const completeCoaMutation =
                    coaMutationHooks.useCompleteCoaMutation();
                const setAllAccounts = useRootStoreSelector(
                    state => state.setAllAccounts,
                );
                const onSaveAccounts = async (coaId: number) => {
                    const state = storeRoot.getState();
                    const toBeDeleted = state.accountsToBeDeleted;
                    const accounts = state.allAccounts.filter(
                        a => !toBeDeleted.includes(a.id),
                    );
                    await saveAccountsMutation.mutateAsync({
                        coaId,
                        accounts,
                    });
                    setAllAccounts(accounts);
                };
                const onCompleteCoA = async (coaId: number) => {
                    await completeCoaMutation.mutateAsync(coaId);
                    navigate.toChartOfAccountsEditMappings({
                        coaId: coaId.toString(),
                    });
                };
                const deleteAccount = useRootStoreSelector(
                    state => state.deleteAccount,
                );
                const restoreAccount = useRootStoreSelector(
                    state => state.restoreAccount,
                );
                const discardCoaMutation =
                    coaMutationHooks.useDiscardCoaMutation();
                const onDiscardCoA = async (coaId?: number) => {
                    if (coaId !== undefined) {
                        await discardCoaMutation.mutateAsync(coaId);
                        navigate.toChartsOfAccountsList();
                    }
                };
                const onClickUpload = (coaId: number) => {
                    navigate.toChartOfAccountsUpload({
                        coaId: coaId.toString(),
                    });
                };

                const onPaste = useRootStoreSelector(
                    state => state.onPasteAccounts,
                );

                const resetAccounts = async (coaId: number) => {
                    await queryClient.invalidateQueries({
                        queryKey: coaKeys.accounts(coaId),
                    });
                    await queryClient.refetchQueries({
                        queryKey: coaKeys.accounts(coaId),
                    });
                    const data = queryClient.getQueryData<
                        Awaited<
                            ReturnType<
                                typeof coaQueryHooks.useAccountsByCoaQuery
                            >["data"]
                        >
                    >(coaKeys.accounts(coaId));
                    if (data) {
                        setAllAccounts(data);
                    }
                };

                return {
                    addAccount,
                    updateAccount,
                    deleteAccount,
                    restoreAccount,
                    onSaveAccounts,
                    resetAccounts,
                    onCompleteCoA,
                    onDiscardCoA,
                    onClickUpload,
                    onPaste,
                    goBack: navigateToChartsOfAccountsEditDefinition,
                    navigateToChartsOfAccountsEditMappings,
                };
            },
        },
    );

    const ViewChartsOfAccountsDetails = createViewChartsOfAccountsDetails({
        useState: ({ coaId }) => {
            const {
                data: allCoAs,
                isPending: allCoAsIsPending,
                isError: allCoAsIsError,
            } = coaQueryHooks.useAllCoasForUserQuery();
            const allCoAsRequestStatus = allCoAsIsPending
                ? "pending"
                : allCoAsIsError
                  ? "rejected"
                  : "resolved";
            const {
                data: accounts,
                isPending: accountsIsPending,
                isError: accountsIsError,
            } = coaQueryHooks.useAccountsByCoaQuery(coaId);
            const accountsRequestStatus = accountsIsPending
                ? "pending"
                : accountsIsError
                  ? "rejected"
                  : "resolved";
            const { data: userData } = userQueryHooks.useUserDataQuery();
            const isGlobalAdmin = userData?.userInfo?.isGlobalAdmin ?? false;
            const userCompanyId = userData?.userInfo?.companyId ?? null;
            const isProdaUser = userData?.userInfo?.isProdaUser ?? false;
            return {
                allCoAs: allCoAs ?? null,
                allCoAsRequestStatus,
                accounts: accounts ?? null,
                accountsRequestStatus,
                isGlobalAdmin,
                userCompanyId,
                isProdaUser,
            };
        },
        useCases: () => {
            const navigateToChartOfAccountsEditDetails = (coaId: number) => {
                navigate.toChartOfAccountsEditDetails({
                    coaId: coaId.toString(),
                });
            };

            return {
                navigateToChartOfAccountsEditDetails,
            };
        },
    });

    const ViewAuthRoleGuard = createViewAuthRoleGuard({
        useState: () => {
            const { data: userData, isPending } =
                userQueryHooks.useUserDataQuery();
            const userRole = userData?.userInfo?.userType || null;
            const isUserRoleLoading = isPending;
            const isProdaUser = userData?.userInfo?.isProdaUser ?? false;
            return { userRole, isUserRoleLoading, isProdaUser };
        },
        useCases: () => {
            const redirectOnRoleNotMatched = React.useCallback(() => {
                navigate.toAppEntrypoint();
            }, []);

            return { redirectOnRoleNotMatched };
        },
    });

    const ViewChartsOfAccountsMappings = createViewChartsOfAccountsMappings({
        useState: ({ coaId }) => {
            const {
                data: allCoAs,
                isPending: allCoAsIsPending,
                isError: allCoAsIsError,
            } = coaQueryHooks.useAllCoasForUserQuery();
            const allCoAsRequestStatus = allCoAsIsPending
                ? "pending"
                : allCoAsIsError
                  ? "rejected"
                  : "resolved";
            const {
                data: accountMappingTableData,
                isPending: mappingIsPending,
                isError: mappingIsError,
            } = coaQueryHooks.useCoaMappingTableQuery(coaId);
            const accountMappingTableStatus = mappingIsPending
                ? "pending"
                : mappingIsError
                  ? "rejected"
                  : "resolved";
            const isSaveCustomMappingsPending =
                useMutationState({
                    filters: {
                        mutationKey: ["coa", "saveCustomAccountMappings"],
                        status: "pending",
                        predicate: mutation =>
                            mutationVariablesHaveCoaId(
                                mutation.state.variables,
                                coaId,
                            ),
                    },
                }).length > 0;
            const saveCustomMappingsMutationStates = useMutationState({
                filters: {
                    mutationKey: ["coa", "saveCustomAccountMappings"],
                    predicate: mutation =>
                        mutationVariablesHaveCoaId(
                            mutation.state.variables,
                            coaId,
                        ),
                },
                select: mutation => ({
                    status: mutation.state.status,
                    submittedAt: mutation.state.submittedAt,
                }),
            });
            const isSaveCustomMappingsError =
                getLatestMutationStatus(saveCustomMappingsMutationStates) ===
                "error";
            const alteredCustomAccounts = useRootStoreSelector(
                state => state.alteredCustomAccounts,
            );
            const { data: userData } = userQueryHooks.useUserDataQuery();
            const isProdaUser = userData?.userInfo?.isProdaUser ?? false;
            const { data: coaDefinition } =
                coaQueryHooks.useCoaDefinitionQuery(coaId);
            const isCoaDraft = coaDefinition?.isDraft ?? false;
            const selectedMappingStatuses = useRootStoreSelector(
                state => state.selectedMappingStatuses,
            );
            const selectedAssetTypes = useRootStoreSelector(
                state => state.selectedAssetTypes,
            );
            const selectVisibleAccountMappingTableData = useRootStoreSelector(
                state => state.selectVisibleAccountMappingTableData,
            );

            const visibleAccountMappingTableData =
                selectVisibleAccountMappingTableData({
                    accountMappingTableData:
                        accountMappingTableData?.mappingTable || null,
                    alteredCustomAccounts,
                    customAccounts:
                        accountMappingTableData?.customAccounts || null,
                    selectedAssetTypes,
                    selectedMappingStatuses,
                });

            const selectAvailableAssetTypes = useRootStoreSelector(
                state => state.selectAvailableAssetTypes,
            );
            const availableAssetTypes = React.useMemo(
                () =>
                    selectAvailableAssetTypes({
                        accountMappingTableData:
                            accountMappingTableData?.mappingTable || null,
                    }),
                [accountMappingTableData, selectAvailableAssetTypes],
            );

            return {
                allCoAs: allCoAs ?? null,
                allCoAsRequestStatus,
                accountMappingTableStatus,
                customAccountsData:
                    accountMappingTableData?.customAccounts || null,
                mappingTable: accountMappingTableData?.mappingTable || null,
                isSaveCustomMappingsPending,
                isSaveCustomMappingsError,
                alteredCustomAccounts,
                visibleAccountMappingTableData,
                isProdaUser,
                isCoaDraft,
                availableAssetTypes,
                selectedAssetTypes,
                selectedMappingStatuses,
            };
        },
        useCases: () => {
            const changeCustomAccountMapping = useRootStoreSelector(
                state => state.changeCustomAccountMapping,
            );
            const saveCustomMappingsMutation =
                coaMutationHooks.useSaveCustomAccountMappingsMutation();
            const navigateToChartsOfAccountsEditDetails = (coaId: number) => {
                navigate.toChartOfAccountsEditDetails({
                    coaId: coaId.toString(),
                });
            };
            const selectAssetTypes = useRootStoreSelector(
                state => state.selectAssetTypes,
            );
            const selectMappingStatuses = useRootStoreSelector(
                state => state.selectMappingStatuses,
            );
            const resetStateCustomChartOfAccountsMappings =
                useRootStoreSelector(
                    state => state.resetStateCustomChartOfAccountsMappings,
                );

            const saveAlteredCustomAccountsHandler = React.useCallback(
                async (options: { coaId: number }) => {
                    const altered = storeRoot.getState().alteredCustomAccounts;
                    await saveCustomMappingsMutation.mutateAsync({
                        coaId: options.coaId,
                        alteredCustomAccounts: altered,
                    });
                    resetStateCustomChartOfAccountsMappings();
                },
                [
                    resetStateCustomChartOfAccountsMappings,
                    saveCustomMappingsMutation,
                ],
            );

            return {
                saveAlteredCustomAccounts: options => {
                    void saveAlteredCustomAccountsHandler(options);
                },
                changeCustomAccountMapping,
                navigateToChartsOfAccountsEditDetails,
                selectAssetTypes,
                selectMappingStatuses,
            };
        },
        ComponentConcurrentUsersIndicator: ViewConcurrentUsersIndicator,
        ComponentConcurrentUsersBanner: ViewConcurrentUsersBanner,
        ComponentConcurrentUsersStartSessionOnMountAndStopOnUnmount:
            HeadlessViewConcurrentUsersStartSessionOnMountAndStopOnUnmount,
    });

    const ViewChartsOfAccountsAuditLog = createViewChartsOfAccountsAuditLog({
        useState: ({ coaId }) => {
            const {
                data: auditLogs,
                isPending: auditLogsIsPending,
                isError: auditLogsIsError,
            } = coaQueryHooks.useCoaAuditLogsQuery(coaId);
            const auditLogsRequestStatus = auditLogsIsPending
                ? "pending"
                : auditLogsIsError
                  ? "rejected"
                  : "resolved";
            const { data: userData } = userQueryHooks.useUserDataQuery();
            const currentUserId = userData?.userInfo?.userId ?? null;
            const {
                data: allCoAs,
                isPending: allCoAsIsPending,
                isError: allCoAsIsError,
            } = coaQueryHooks.useAllCoasForUserQuery();
            const allCoAsRequestStatus = allCoAsIsPending
                ? "pending"
                : allCoAsIsError
                  ? "rejected"
                  : "resolved";
            const isProdaUser = userData?.userInfo?.isProdaUser ?? false;

            return {
                auditLogs: auditLogs ?? null,
                auditLogsRequestStatus,
                currentUserId,
                allCoAs: allCoAs ?? null,
                allCoAsRequestStatus,
                isProdaUser,
            };
        },
    });

    return {
        store: storeRoot,
        queryClient,
        OSQueryProvider,
        ViewAuthInit,
        ViewFileUpload,
        ViewCoAUpload,
        ViewFileExtractionModal,
        ViewBreadcrumbs,
        ViewDatabaseSelector,
        ViewDealList,
        ViewDashboard,
        ViewDealCreation,
        ViewDealOverview,
        ViewDealDetails,
        ViewDealActivities,
        ViewToasts,
        ViewExtractionSidebar,
        ViewExtractionStandardizedSidebar,
        ViewSettingsMenu,
        ViewTopBar,
        ViewNewExperienceSection,
        ViewNavigationSidebar,
        ViewDatabaseGuard,
        ViewSelectDatabaseModal,
        ViewUserConsentModal,
        ViewAuthCallback,
        ViewAuthGuard,
        ViewThirdPartyDoc,
        ViewExtraction,
        ViewExtractionReview,
        ViewExportModal,
        ViewUserPolicies,
        ViewCreateChartOfAccounts,
        ViewChartsOfAccountsDashboard,
        ViewEditChartOfAccountsDefinition,
        ViewEditChartOfAccountsDetails,
        ViewChartsOfAccountsDetails,
        ViewAuthRoleGuard,
        ViewChartsOfAccountsMappings,
        ViewChartsOfAccountsAuditLog,
        ViewExtractionAnnualized,
        ViewExtractionAnnualizedOptions,
        ViewExtractionAnnualizedAdditionalOptions,
        ViewProjectDocuments,
    };
};
